<template>
	<div class="widget-builder">
		<LoadingOverlay :visible="isPageLoading || isKpiDetailLoading" :message="t('Loading')" />

		<v-card class="filters-card" flat>
			<v-card-title>
				<UserViewControl :view-key="'KpiBuilder'" :get-view-state="getUserViewState" :apply-view-state="applyUserViewState" :default-view-state="getDefaultUserViewState" :is-ready="isUserViewReady" :state-version="viewStateVersion" @view-applied="onUserViewApplied">
					<template #actions>
						<v-badge :model-value="activeFilterCount > 0" :content="activeFilterCount" color="primary">
							<v-btn icon="filter_alt" variant="tonal" color="primary" density="comfortable" :disabled="!isUserViewReady" @click="isFilterSheetOpen = true" />
						</v-badge>
						<FilterField :filter="kpiReferenceFilter" class="widget-builder__kpi-select" @ref="setKpiReferenceControl" @change="onKpiReferenceChanged" />
						<v-switch v-model="allPark" :label="t('AllPark')" class="widget-builder__all-park" color="primary" hide-details density="compact" inset />
					</template>
				</UserViewControl>

				<FilterChips :chips="selectedFilterChips" @select="onFilterChipClick" @remove="removeFilterChip" @clear="resetFilters" />
			</v-card-title>

			<FilterControls ref="filterControls" :filters="filterDefinitions" :mobile="isLaptop" layout="panel" :show-more-toggle="true" v-model:open="isFilterSheetOpen" v-model:show-additional="showAdditionalFilters" @apply="onFiltersApply" @reset="resetFilters" @update:active-count="activeFilterCount = $event" />
		</v-card>

		<v-expansion-panels v-model="expandedKpiPanel">
			<v-expansion-panel :value="KPI_LIST_PANEL_KEY" class="widget-builder--panel" eager>
				<v-expansion-panel-title>
					<v-icon>swap_driving_apps_wheel</v-icon>
					{{ t('Kpis') }}
					<v-badge color="primary" :content="kpiComponents.length + 1" inline></v-badge>
				</v-expansion-panel-title>
				<v-expansion-panel-text>
					<transition-group name="pop" tag="div" class="kpi-list">
						<div class="main-kpi" key="main-kpi">
							<Kpi :title="mainKpiTitle" :color="props.color" :width="props.width" :params="mainKpiParams" :annotation="t('KpiGlobal')" :need-title="false" @kpi-apply-filters="reapplyKpiFilters" />
						</div>
						<div class="custom-kpi" v-for="(customKpi, index) in kpiComponents" :key="customKpi.guid || index">
							<Kpi :title="customKpi.title" :color="customKpi.color" :width="customKpi.width" :params="customKpi.params" :annotation="customKpi.annotation" :custom="true" :guid="customKpi.guid" :need-title="false" @kpi-apply-filters="reapplyKpiFilters" />
						</div>
						<div v-if="hasAppliedFilters" class="add-custom-kpi" key="add-custom-kpi">
							<v-tooltip :attach="overlayTarget" location="bottom">
								<template v-slot:activator="{ props }">
									<v-btn v-bind="props" icon="add_2" @click="createCustomKpiManually" variant="tonal" color="primary" size="x-large" class="pulse"></v-btn>
								</template>
								<span>{{ t('AddNewKpi') }}</span>
							</v-tooltip>
						</div>
					</transition-group>
				</v-expansion-panel-text>
			</v-expansion-panel>
		</v-expansion-panels>

		<KpiClusterPanel :clusters="clusterKpiItems" :color="props.color" :width="props.width" />

		<DetailGridCard :title="detailTableTitle" :count="displayedDeliveryLocationCount" v-model:search="kpiDetailSearch">
			<template #chips>
				<v-chip :color="isDetailSimulated ? 'primary' : 'default'" variant="tonal" readonly>
					<v-icon>{{ isDetailSimulated ? 'play_circle' : 'radio_button_checked' }}</v-icon>
					{{ isDetailSimulated ? t('SimulatedData') : t('RealData') }}
				</v-chip>
			</template>
			<template #groups>
				<QuickRowGroup v-if="!isLaptop && viewMode === 'store'" v-model="selectedGridRowGroups" :groups="quickRowGroupDefinitions" :default-active-groups="defaultActiveGridRowGroups" />
				<QuickRowGroup v-if="!isLaptop && viewMode === 'product'" v-model="selectedProductGridRowGroups" :groups="productQuickRowGroupDefinitions" :default-active-groups="defaultActiveProductGridRowGroups" />
				<v-btn v-if="canShowProductGrainToggle" @click="toggleProductGrain" :loading="isProductFactLoading" :disabled="isKpiDetailLoading" variant="tonal" :color="viewMode === 'product' ? 'primary' : 'secondary'" rounded="xl" class="ml-2">
					<v-icon>{{ viewMode === 'product' ? 'storefront' : 'inventory_2' }}</v-icon>
					{{ viewMode === 'product' ? t('DeliveryLocationView') : t('ProductView') }}
				</v-btn>
			</template>

			<v-alert v-if="kpiDetailError" type="error" variant="tonal" density="comfortable" class="mb-4">
				{{ kpiDetailError }}
			</v-alert>

			<div class="kpi-detail-card__grid-shell">
				<div v-show="showKpiDetailGrid" class="kpi-detail-card__grid">
					<AgGrid
						v-if="!isLaptop"
						ref="kpiDetailGrid"
						:rowData="activeDetailRows"
						:getRowId="getKpiDetailRowId"
						:colDefs="activeDetailColumns"
						:getGroupRowAgg="activeGroupRowAgg"
						groupDisplayType="singleColumn"
						:sideBar="isTouchMode ? false : true"
						:row-group-panel-show="isTouchMode ? 'false' : 'always'"
						:pagination="true"
						:paginationPageSize="50"
						@displayed-row-count-changed="onDisplayedRowCountChanged"
						@row-group-columns-changed="onGridRowGroupColumnsChanged"
						@grid-ready="onKpiDetailGridReady"
						@view-state-changed="onDetailGridViewStateChanged"
					/>

					<TouchCardData
						v-else
						:row-data="filteredKpiDetailRows"
						:columns="touchDetailColumns"
						:title-field="['deliveryLocation', 'companyName']"
						:subtitle-fields="['postcode', 'city', 'channel']"
						row-key="id"
						metric-layout="rows"
						:groups="quickRowGroupDefinitions"
						v-model:group-columns="selectedGridRowGroups"
						:default-active-groups="defaultActiveGridRowGroups"
						:group-fields="rowGroupFieldByColId"
						:get-group-row-agg="getKpiDetailGroupRowAgg"
						:group-leaf-label="t('DeliveryLocations')"
						leaf-count-field="deliveryLocationId"
						sort-storage-key="kpiDetail"
					/>
				</div>

				<v-sheet v-if="!showKpiDetailGrid || (!isKpiDetailLoading && !kpiDetailError && activeDetailRows.length === 0)" class="kpi-detail-card__empty kpi-detail-card__empty--overlay" rounded>
					<template v-if="!showKpiDetailGrid && hasAppliedFilters">
						<v-btn @click="showDetailGrid" color="primary" variant="tonal" rounded="xl" size="large" class="mt-4">
							<v-icon>visibility</v-icon>
							{{ t('ShowDetailGrid') }}
						</v-btn>
					</template>
					<template v-else>
						<v-icon>table_rows_narrow</v-icon>
						<span>{{ detailEmptyStateMessage }}</span>
					</template>
				</v-sheet>
			</div>
		</DetailGridCard>
	</div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, inject, watch, nextTick } from 'vue';
import { useTranslator } from '@/composables/useTranslator';
import { iframeDataBridge } from '@/iframe/iframeDataBridge';
import { kpiService } from '@/services/kpiService';
import { eventBus } from '@/utils/core/eventBus';
import { deviceUtils } from '@/utils/core/deviceUtils';
import { buildFilterChips, scrollFilterFieldIntoView } from '@/utils/ui/filterChips';

import Kpi from '@/components/kpi/Kpi.vue';
import KpiClusterPanel from '@/components/kpi/KpiClusterPanel.vue';
import DetailGridCard from '@/components/kpi/DetailGridCard.vue';
import AgGrid from '@/components/agGrid/AgGrid.vue';
import TouchCardData from '@/components/agGrid/TouchCardData.vue';
import ProgressBarRenderer from '@/components/agGrid/ProgressBarRenderer.vue';
import CompanyNameRenderer from '@/components/agGrid/CompanyNameRenderer.vue';
import FilterControls from '@/components/controls/FilterControls.vue';
import FilterField from '@/components/controls/FilterField.vue';
import FilterChips from '@/components/controls/FilterChips.vue';
import UserViewControl from '@/components/controls/UserViewControl.vue';

import { getOrganisationsTree, getOrganisationMaxLevel } from '@/api/core/organisation';
import { getAccounts } from '@/api/distributor/account';
import { getChannels } from '@/api/distributor/channel';
import { getProductTreesTree } from '@/api/core/productTree';
import { getAttributeById, getAttributes } from '@/api/core/attribute';
import { getDeliveryLocationAttributes } from '@/api/sales/deliveryLocationAttribute';
import { getCompanies } from '@/api/core/company';
import { getUserSettings, postUserSettings } from '@/api/core/userSetting';
import QuickRowGroup from '@/components/agGrid/QuickRowGroup.vue';
import LoadingOverlay from '@/components/common/LoadingOverlay.vue';

const t = useTranslator();
const ecosVM = iframeDataBridge.getEcosVM();
const user = iframeDataBridge.getCurrentUser();
const toast = iframeDataBridge.getIframeWindow().toast;
const overlayTarget = inject('overlayTarget', null);
const KPI_BUILDER_CUSTOMS_SETTING_NAME = 'VueCustomKpis';
const isTouchMode = ref(deviceUtils.isTouchMode());
const isLaptop = ref(deviceUtils.isLaptop());
const onResize = () => {
	isTouchMode.value = deviceUtils.isTouchMode();
	isLaptop.value = deviceUtils.isLaptop();
};

const props = defineProps({
	title: {
		type: String,
		required: true,
	},
	color: {
		type: String,
		default: 'primary',
	},
	width: {
		type: Number,
		default: 12,
	},
	params: {
		type: Object,
		default: () => ({}),
	},
	guid: {
		type: String,
		default: null,
	},
});

const error = ref(null);
const sortedWidgets = inject('sortedWidgets', ref([]));
const activeKpiReference = ref(props.params.reference || '');
const userSettings = ref([]);

const kpis = ref([]);
const organisations = ref([]);
const deliveryLocationTypes = ref([]);
const accounts = ref([]);
const channels = ref([]);
const productTrees = ref([]);
const specificities = ref([]);
const companies = ref([]);

// The filter controls now live inside FilterControls (side panel on laptop+, bottom sheet below), which
// republishes its child instances: these computeds keep every existing `xxxSelect.value?.getValues()`
// call site working unchanged.
const filterControls = ref(null);
const controlOf = (key) => computed(() => filterControls.value?.controls?.[key] || null);
// The KPI select moved out of the filter panel into the header (beside UserViewControl), so it is no
// longer republished by FilterControls: it is held directly, which keeps every existing
// `kpiReferenceSelect.value?.getValues()/getFullValues()/selectItems()` call site working unchanged.
const kpiReferenceSelect = ref(null);
const organisationTreeview = controlOf('organisationTreeview');
const deliveryLocationTypeSelect = controlOf('deliveryLocationTypeSelect');
const accountSelect = controlOf('accountSelect');
const channelSelect = controlOf('channelSelect');
const productTreeTreeview = controlOf('productTreeTreeview');
const statementProductTreeTreeview = controlOf('statementProductTreeTreeview');
const specificitySelect = controlOf('specificitySelect');
const companySelect = controlOf('companySelect');
const labelTokens = controlOf('labelTokens');
const eanTokens = controlOf('eanTokens');
const referenceTokens = controlOf('referenceTokens');
const excludeSharedOrganisationsToggle = controlOf('excludeSharedOrganisationsToggle');
const showAdditionalFilters = ref(false);
const isFilterSheetOpen = ref(false);
const activeFilterCount = ref(0);
const allPark = ref(true);
const showKpiDetailGrid = ref(false);
const selectedFilterChips = ref([]);

const kpiDetailRows = ref([]);
const kpiDetailSearch = ref('');
const isKpiDetailLoading = ref(false);
const isPageLoading = ref(true);
const kpiDetailError = ref(null);
const deliveryLocationAttributeColumns = ref([]);
const attributesById = ref(new Map());
const kpiDetailGrid = ref(null);
const lastKnownGridState = ref(null);
const selectedGridRowGroups = ref([]);
const displayedDeliveryLocationCount = ref(0);
// Product-grain "grain change" (RG-19/22): a deliberate toggle, not an auto-recompute — independent quick-group
// state so a saved product-grain grouping is naturally ignored while in store view and restored on toggle-back.
const viewMode = ref('store');
const kpiProductFactRows = ref([]);
const isProductFactLoading = ref(false);
const productFactError = ref(null);
const selectedProductGridRowGroups = ref([]);
const PRODUCT_GRAIN_VOLUMETRY_THRESHOLD = 20000;
const isDetailSimulated = ref(Boolean(props.params?.simulated));
const currentAppliedFilters = ref(null);
const viewStateVersion = ref(0);
const isKpiDetailGridReady = ref(false);
const isUserViewReady = ref(false);
const detailGridReadyResolvers = [];
const USER_VIEW_DEFAULT_ID = '__default__';

const GRID_GROUP_COLUMNS = {
	DELIVERY_LOCATION: 'rowGroupDeliveryLocation',
	ACCOUNT: 'rowGroupAccount',
	CHANNEL: 'rowGroupChannel',
	DELIVERY_LOCATION_TYPE: 'rowGroupDeliveryLocationType',
	LOCATION_GROUP: 'rowGroupLocationGroup',
	SPECIFICITIES: 'rowGroupSpecificities',
	PRODUCT: 'rowGroupProduct',
};
let kpiDetailLoadRequestId = 0;
let productFactLoadRequestId = 0;
let latestKpiDetailLoadKey = '';
let isApplyingViewState = false;
const deliveryLocationAttributeCache = new Map();

const filterRefs = {
	kpiReferenceSelect,
	organisationTreeview,
	deliveryLocationTypeSelect,
	accountSelect,
	channelSelect,
	statementProductTreeTreeview,
	productTreeTreeview,
	specificitySelect,
	companySelect,
	labelTokens,
	eanTokens,
	referenceTokens,
	excludeSharedOrganisationsToggle,
};

const KPI_LIST_PANEL_KEY = 'kpi-list';
const localKpiComponents = ref([]);
const expandedKpiPanel = ref(KPI_LIST_PANEL_KEY);
const hasAppliedFilters = ref(false);

const preSelectedKpi = computed(() => findKpiByReference(activeKpiReference.value));
const kpiReferenceFilter = computed(() => ({ key: 'kpiReferenceSelect', type: 'select', label: t('Kpis'), items: kpis.value, titleKey: 'referenceTranslated', itemValue: '_uid', required: true, preSelected: preSelectedKpi.value?._uid ?? null, density: 'comfortable' }));

const filterDefinitions = computed(() => [
	{ key: 'organisationTreeview', type: 'treeview', label: t('Organisations'), items: organisations.value, titleKey: 'label', multiple: true, section: 'perimeter', branchSelection: true, selectAll: true, showSelectedCount: true, expandOnSearch: true, searchFields: ['title', 'reference'], itemsRegistration: 'props' },
	{ key: 'excludeSharedOrganisationsToggle', type: 'toggle', label: t('ExcludeSharedOrganisations'), section: 'perimeter' },
	{ key: 'accountSelect', type: 'select', label: t('Accounts'), items: accounts.value, multiple: true, section: 'pointOfSale' },
	{ key: 'deliveryLocationTypeSelect', type: 'select', label: t('DeliveryLocationType'), items: deliveryLocationTypes.value, multiple: true, section: 'pointOfSale' },
	{ key: 'channelSelect', type: 'select', label: t('Channels'), items: channels.value, multiple: true, section: 'pointOfSale' },
	{ key: 'companySelect', type: 'select', label: t('Companies'), items: companies.value, multiple: true, titleKey: 'companyName', section: 'pointOfSale' },
	{ key: 'statementProductTreeTreeview', type: 'treeview', label: t('StatementCategories'), items: statementCategoryTree.value, titleKey: 'label', multiple: true, section: 'product' },
	{ key: 'productTreeTreeview', type: 'treeview', label: t('ProductCategory'), items: productTrees.value, titleKey: 'label', multiple: true, section: 'product' },
	{ key: 'specificitySelect', type: 'select', label: t('Specificities'), items: specificities.value, multiple: true, titleKey: 'value', section: 'product' },
	{ key: 'labelTokens', type: 'tokenlist', label: t('ProductLabel'), icon: 'sell', section: 'product' },
	{ key: 'eanTokens', type: 'tokenlist', label: t('Ean'), icon: 'qr_code', section: 'product' },
	{ key: 'referenceTokens', type: 'tokenlist', label: t('ProductReference'), icon: 'tag', section: 'product' },
]);

const matchingWidgets = computed(() => {
	if (!Array.isArray(sortedWidgets.value)) {
		return [];
	}

	return sortedWidgets.value.filter((widget) => hasSameReference(widget?.params?.reference, activeKpiReference.value));
});
const mainWidget = computed(() => matchingWidgets.value.find((widget) => !widget.guid) || matchingWidgets.value.find((widget) => widget.id === props.guid) || matchingWidgets.value[0] || null);
const mainKpiTitle = computed(() => mainWidget.value?.title || getKpiTitle(activeKpiReference.value) || props.title);
const mainKpiParams = computed(() => {
	const params = mainWidget.value?.params || props.params || {};
	const baseParams = {
		reference: activeKpiReference.value || params.reference,
		type: params.type || 'kpi',
		shape: params.shape || 'circle',
		allPark: allPark.value,
	};

	if (params.icon) {
		baseParams.icon = params.icon;
	}

	if ('simulated' in params) {
		baseParams.simulated = params.simulated;
	}

	return baseParams;
});
const persistedKpiComponents = computed(() => matchingWidgets.value.filter((widget) => widget.custom || !!widget.guid).map((widget) => mapWidgetToCustomKpi(widget)));
const kpiComponents = computed(() => {
	if (!hasAppliedFilters.value) {
		return [];
	}

	const mergedComponents = new Map();

	persistedKpiComponents.value.forEach((customKpi) => {
		mergedComponents.set(customKpi.guid, customKpi);
	});

	localKpiComponents.value
		.filter((customKpi) => hasSameReference(customKpi.params?.reference, activeKpiReference.value))
		.filter((customKpi) => !mergedComponents.has(customKpi.guid))
		.forEach((customKpi) => {
			mergedComponents.set(customKpi.guid, customKpi);
		});

	return Array.from(mergedComponents.values());
});
const detailTableTitle = computed(() => mainKpiTitle.value?.trim() || t('Kpis'));
const clusterKpiItems = computed(() => {
	if (!hasAppliedFilters.value || !currentAppliedFilters.value?.reference) {
		return [];
	}

	const clustersById = new Map();
	(Array.isArray(kpiDetailRows.value) ? kpiDetailRows.value : []).forEach((row) => {
		const rawClusterId = Number(row?.clusterId);
		if (!Number.isInteger(rawClusterId) || rawClusterId <= 0) {
			return;
		}

		if (!clustersById.has(rawClusterId)) {
			clustersById.set(rawClusterId, row?.cluster || `${t('Cluster')} ${rawClusterId}`);
		}
	});

	return Array.from(clustersById.entries())
		.map(([clusterId, clusterName]) => ({
			key: `cluster-${clusterId}`,
			title: mainKpiTitle.value,
			annotation: clusterName,
			params: {
				...currentAppliedFilters.value,
				clusterId,
			},
		}))
		.sort((firstItem, secondItem) => firstItem.annotation.localeCompare(secondItem.annotation));
});
const detailEmptyStateMessage = computed(() => {
	if (!activeKpiReference.value) {
		return t('PleaseSelectKpi');
	}

	return t('NoData');
});
const isDetailPercentage = computed(() => kpiService.isPercentage(activeKpiReference.value || props.params.reference || ''));
// Level as the grid can actually FILL it: how deep the node sits in the tree we were served, never the
// server-side `depth` field (absolute, 1-indexed, and unaffected by a tree truncated by product-tree read
// restrictions). Reading `depth` here reported one level too many, which every consumer then showed as a
// phantom, always-blank "Niveau relevé N" / "Cat. produit N". Same measure as
// kpiService._getProductTreeMaxLevel, which pads the row fields these columns display.
const productTreeMaxLevel = computed(() => getTreeMaxLevel(productTrees.value));

function getTreeMaxLevel(nodes = [], parentLevel = 0) {
	return (Array.isArray(nodes) ? nodes : []).reduce((maxLevel, node) => {
		const level = node?.children?.length ? getTreeMaxLevel(node.children, parentLevel + 1) : parentLevel + 1;
		return Math.max(maxLevel, level);
	}, parentLevel);
}
// Org-configured depth at which SFA statements are created (SFAStatementLevel/RankLevel). Caps the
// "catégorie du relevé" dimension: every level beyond it is structurally always blank (see
// kpiService._buildProductTreeLevels), so hiding them hides nothing.
const statementCategoryLevel = ref(null);
const cappedCategoryLevel = computed(() => {
	const max = productTreeMaxLevel.value || 0;
	const cap = statementCategoryLevel.value;
	if (!Number.isFinite(cap) || cap <= 0) return max;
	return max > 0 ? Math.min(cap, max) : cap;
});
// Items of the "Catégories relevé" filter: the same nested tree as "Catégorie produit", pruned to the
// statement level. Deeper nodes do exist, but no statement is ever created on one (see
// kpiService.getStatementCategoryLevel), so offering them would offer filters that structurally match
// nothing — pruning also turns the statement-level nodes into the tree's leaves.
const statementCategoryTree = computed(() => pruneTreeToDepth(productTrees.value, cappedCategoryLevel.value));
const defaultDetailGridRowGroups = computed(() => [GRID_GROUP_COLUMNS.DELIVERY_LOCATION, ...Array.from({ length: cappedCategoryLevel.value || 0 }, (_, index) => `rowGroupProductTreeLevel${index + 1}`)]);
const defaultDetailGridVisibleColumns = computed(() => ['companyName', 'value', 'difference', 'referenceCount', 'totalReferencesCount', ...cappedCategoryLevelFields('productTreeLevel')]);
selectedGridRowGroups.value = [...defaultDetailGridRowGroups.value];
const defaultActiveGridRowGroups = ref([...defaultDetailGridRowGroups.value]);
function cappedCategoryLevelFields(prefix) {
	return Array.from({ length: cappedCategoryLevel.value || 0 }, (_, index) => `${prefix}${index + 1}`);
}

// Keeps the nodes down to maxDepth levels and drops everything below (children emptied, not deleted:
// TreeViewControl strips empty children arrays itself). maxDepth <= 0 means "unknown level, no pruning".
function pruneTreeToDepth(nodes = [], maxDepth = 0, currentDepth = 1) {
	if (!Array.isArray(nodes)) {
		return [];
	}

	if (maxDepth <= 0) {
		return nodes;
	}

	return nodes.map((node) => ({
		...node,
		children: currentDepth < maxDepth ? pruneTreeToDepth(node?.children, maxDepth, currentDepth + 1) : [],
	}));
}

const organisationRowGroupColumns = computed(() => {
	return Array.from({ length: organisationMaxLevel.value || 0 }, (_, index) => {
		const level = index + 1;
		return {
			colId: `rowGroupOrganisationLevel${level}`,
			field: `organisationLevel${level}`,
			headerName: `${t('Organisation')} ${t('Level')} ${level}`,
			hide: true,
			enableRowGroup: true,
			suppressColumnsToolPanel: true,
		};
	});
});
const categoryRowGroupColumns = computed(() => {
	return Array.from({ length: cappedCategoryLevel.value || 0 }, (_, index) => {
		const level = index + 1;
		return {
			colId: `rowGroupProductTreeLevel${level}`,
			field: `productTreeLevel${level}`,
			headerName: `${t('StatementCategoryLevel')} ${level}`,
			hide: true,
			enableRowGroup: true,
			suppressColumnsToolPanel: true,
		};
	});
});

const quickRowGroupDefinitions = computed(() => {
	const groups = [{ key: 'deliveryLocation', label: t('DeliveryLocation'), columnField: GRID_GROUP_COLUMNS.DELIVERY_LOCATION }];

	if (organisationMaxLevel.value > 0) {
		groups.push({
			key: 'organisation',
			label: t('Organisation'),
			levels: Array.from({ length: organisationMaxLevel.value }, (_, index) => ({
				key: `organisation-level-${index + 1}`,
				label: `${t('Level')} ${index + 1}`,
				columnField: `rowGroupOrganisationLevel${index + 1}`,
			})),
		});
	}

	if (cappedCategoryLevel.value > 0) {
		groups.push({
			key: 'categories',
			label: t('StatementCategories'),
			levels: Array.from({ length: cappedCategoryLevel.value }, (_, index) => ({
				key: `producttree-level-${index + 1}`,
				label: `${t('StatementCategoryLevel')} ${index + 1}`,
				columnField: `rowGroupProductTreeLevel${index + 1}`,
			})),
		});
	}

	groups.push({ key: 'account', label: t('Account'), columnField: GRID_GROUP_COLUMNS.ACCOUNT }, { key: 'channel', label: t('Channel'), columnField: GRID_GROUP_COLUMNS.CHANNEL }, { key: 'locationGroup', label: t('Group'), columnField: GRID_GROUP_COLUMNS.LOCATION_GROUP }, { key: 'specificities', label: t('Specificities'), columnField: GRID_GROUP_COLUMNS.SPECIFICITIES });

	return groups;
});
// "Catégorie produit" (RG-26): unlike categoryRowGroupColumns above, this iterates the UNCAPPED
// productTreeMaxLevel — a product's own category can legitimately go deeper than the org's statement level.
const productCategoryRowGroupColumns = computed(() => {
	return Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => {
		const level = index + 1;
		return {
			colId: `rowGroupProductCategoryLevel${level}`,
			field: `productCategoryLevel${level}`,
			headerName: `${t('ProductCategory')} ${t('Level')} ${level}`,
			hide: true,
			enableRowGroup: true,
			suppressColumnsToolPanel: true,
		};
	});
});
const defaultProductGridRowGroups = computed(() => [GRID_GROUP_COLUMNS.DELIVERY_LOCATION]);
const defaultActiveProductGridRowGroups = ref([...defaultProductGridRowGroups.value]);
// Separate from quickRowGroupDefinitions (not gated the same way): only available in product view, so the
// "Produit" axis structurally cannot be selected in store view (RG-26) rather than merely being disabled.
// Order: Lieu de livraison, Organisation, Cat. relevé, Enseigne, Circuits, Groupe, Cat. produit, Produit,
// Spécificités — same Organisation-right-after-the-store-axis order as the store view above. Cat. relevé
// reuses the same capped, inherited-from-statement levels as the store view (RG-24: available in both
// views) — product-grain rows carry rowGroupProductTreeLevel* too, see the StatementCategories column
// group in kpiProductFactColumns.
const productQuickRowGroupDefinitions = computed(() => {
	const groups = [{ key: 'deliveryLocation', label: t('DeliveryLocation'), columnField: GRID_GROUP_COLUMNS.DELIVERY_LOCATION }];

	if (organisationMaxLevel.value > 0) {
		groups.push({
			key: 'organisation',
			label: t('Organisation'),
			levels: Array.from({ length: organisationMaxLevel.value }, (_, index) => ({
				key: `organisation-level-${index + 1}`,
				label: `${t('Level')} ${index + 1}`,
				columnField: `rowGroupOrganisationLevel${index + 1}`,
			})),
		});
	}

	if (cappedCategoryLevel.value > 0) {
		groups.push({
			key: 'categories',
			label: t('StatementCategories'),
			levels: Array.from({ length: cappedCategoryLevel.value }, (_, index) => ({
				key: `producttree-level-${index + 1}`,
				label: `${t('StatementCategoryLevel')} ${index + 1}`,
				columnField: `rowGroupProductTreeLevel${index + 1}`,
			})),
		});
	}

	groups.push({ key: 'account', label: t('Account'), columnField: GRID_GROUP_COLUMNS.ACCOUNT }, { key: 'channel', label: t('Channel'), columnField: GRID_GROUP_COLUMNS.CHANNEL }, { key: 'locationGroup', label: t('Group'), columnField: GRID_GROUP_COLUMNS.LOCATION_GROUP });

	if (productTreeMaxLevel.value > 0) {
		groups.push({
			key: 'productCategories',
			label: t('ProductCategory'),
			levels: Array.from({ length: productTreeMaxLevel.value }, (_, index) => ({
				key: `product-category-level-${index + 1}`,
				label: `${t('ProductCategory')} ${index + 1}`,
				columnField: `rowGroupProductCategoryLevel${index + 1}`,
			})),
		});
	}

	groups.push({ key: 'product', label: t('Product'), columnField: GRID_GROUP_COLUMNS.PRODUCT }, { key: 'specificities', label: t('Specificities'), columnField: GRID_GROUP_COLUMNS.SPECIFICITIES });

	return groups;
});
const kpiDetailColumns = computed(() => [
	{
		colId: GRID_GROUP_COLUMNS.DELIVERY_LOCATION,
		field: 'deliveryLocation',
		headerName: t('DeliveryLocation'),
		hide: true,
		enableRowGroup: true,
		rowGroup: true,
		rowGroupIndex: 0,
		suppressColumnsToolPanel: true,
		valueGetter: ({ data }) => data?.deliveryLocation || data?.companyName || '',
	},
	{
		colId: GRID_GROUP_COLUMNS.ACCOUNT,
		field: 'account',
		headerName: t('Account'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.CHANNEL,
		field: 'channel',
		headerName: t('Channel'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.LOCATION_GROUP,
		field: 'locationGroup',
		headerName: t('Group'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.SPECIFICITIES,
		field: 'specificities',
		headerName: t('Specificities'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	...categoryRowGroupColumns.value,
	...organisationRowGroupColumns.value,
	{
		headerName: t('DeliveryLocation'),
		groupId: 'deliveryLocation',
		openByDefault: false,
		marryChildren: true,
		children: [
			{
				field: 'companyName',
				headerName: t('CompanyName'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('companyName'),
				resizable: true,
				minWidth: isTouchMode.value ? 150 : 220,
				cellRenderer: CompanyNameRenderer,
			},
			{
				field: 'parentAccount',
				headerName: t('ParentAccount'),
				sortable: true,
				filter: true,
				hide: true,
				resizable: true,
				minWidth: 160,
			},
			{
				field: 'account',
				headerName: t('Account'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('account'),
				resizable: true,
				minWidth: 160,
			},
			{
				field: 'channel',
				headerName: t('Channel'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('channel'),
				resizable: true,
				minWidth: 140,
			},
			{
				field: 'deliveryLocationType',
				headerName: t('DeliveryLocationType'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('deliveryLocationType'),
				resizable: true,
				minWidth: 160,
				columnGroupShow: 'open',
			},
			{
				field: 'address',
				headerName: t('Address'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('address'),
				resizable: true,
				minWidth: 220,
				columnGroupShow: 'open',
			},
			{
				field: 'postcode',
				headerName: t('Post Code'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('postcode'),
				resizable: true,
				minWidth: 120,
				columnGroupShow: 'open',
			},
			{
				field: 'city',
				headerName: t('City'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('city'),
				resizable: true,
				minWidth: 140,
				columnGroupShow: 'open',
			},
			{
				field: 'posType',
				headerName: t('PosType'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('posType'),
				resizable: true,
				minWidth: 140,
				columnGroupShow: 'open',
			},
			{
				field: 'locationGroup',
				headerName: t('Group'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('locationGroup'),
				resizable: true,
				minWidth: 140,
				columnGroupShow: 'open',
			},
			{
				field: 'distributionChannel',
				headerName: t('DistributionChannel'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('distributionChannel'),
				resizable: true,
				minWidth: 160,
				columnGroupShow: 'open',
			},
			{
				field: 'potential',
				headerName: t('Potential'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('potential'),
				resizable: true,
				minWidth: 120,
				cellDataType: 'number',
				columnGroupShow: 'open',
			},
			{
				field: 'seasonality',
				headerName: t('DeliveryLocationSeasonality'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('seasonality'),
				resizable: true,
				minWidth: 160,
				columnGroupShow: 'open',
			},
			...deliveryLocationAttributeColumns.value,
		],
	},
	{
		field: 'organisations',
		headerName: t('Organisation'),
		sortable: true,
		filter: true,
		hide: true,
		resizable: true,
		width: 180,
		children: Array.from({ length: organisationMaxLevel.value || 0 }, (_, index) => ({
			field: `organisationLevel${index + 1}`,
			headerName: `${t('Level')} ${index + 1}`,
			sortable: true,
			filter: true,
			resizable: true,
			hide: isDefaultDetailGridColumnHidden(`organisationLevel${index + 1}`),
			width: 180,
			columnGroupShow: index === 0 ? null : 'open',
		})),
	},
	{
		field: 'value',
		headerName: detailTableTitle.value,
		sortable: true,
		filter: true,
		resizable: true,
		minWidth: isTouchMode.value ? 150 : 240,
		cellDataType: 'number',
		cellRenderer: ProgressBarRenderer,
		cellRendererParams: () => ({
			formatMetric: formatKpiMetric,
			objectiveLabel: t('Objective'),
			needLabel: false,
			isPercentage: isDetailPercentage.value,
			bufferColor: 'grey-lighten-1',
			bufferOpacity: 0.35,
		}),
		valueFormatter: ({ value }) => formatKpiMetric(value),
		sort: 'desc',
	},
	{
		field: 'objective',
		headerName: t('Objective'),
		sortable: true,
		filter: true,
		hide: isDefaultDetailGridColumnHidden('objective'),
		resizable: true,
		maxWidth: 200,
		cellDataType: 'number',
		enableValue: true,
		valueFormatter: ({ value }) => formatKpiMetric(value),
	},
	{
		field: 'difference',
		headerName: t('Difference'),
		sortable: true,
		filter: true,
		hide: isDefaultDetailGridColumnHidden('difference'),
		resizable: true,
		maxWidth: 200,
		cellDataType: 'number',
		enableValue: true,
		valueFormatter: ({ value }) => formatKpiMetric(value, true),
		cellStyle: ({ value }) => getDifferenceCellStyle(value),
	},
	{
		headerName: t('StatementCategories'),
		groupId: 'categories',
		openByDefault: false,
		marryChildren: true,
		hide: true,
		children: [
			...Array.from({ length: cappedCategoryLevel.value || 0 }, (_, index) => ({
				field: `productTreeLevel${index + 1}`,
				headerName: `${t('StatementCategoryLevel')} ${index + 1}`,
				sortable: true,
				filter: true,
				resizable: true,
				hide: isDefaultDetailGridColumnHidden(`productTreeLevel${index + 1}`),
				minWidth: 180,
				columnGroupShow: null,
			})),
		],
	},
	{
		field: 'specificities',
		headerName: t('Specificities'),
		sortable: true,
		filter: true,
		hide: isDefaultDetailGridColumnHidden('specificities'),
		resizable: true,
		minWidth: 220,
	},
	{
		headerName: t('CodesCount'),
		groupId: 'codesCount',
		openByDefault: false,
		marryChildren: true,
		hide: false,
		children: [
			{
				field: 'referenceCount',
				headerName: t('ReferencesCount'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('referenceCount'),
				resizable: true,
				maxWidth: 200,
				cellDataType: 'number',
				valueFormatter: ({ value }) => formatReferenceCount(value),
			},
			{
				field: 'totalReferencesCount',
				headerName: t('TotalReferencesCount'),
				sortable: true,
				filter: true,
				hide: isDefaultDetailGridColumnHidden('totalReferencesCount'),
				resizable: true,
				maxWidth: 220,
				cellDataType: 'number',
				valueFormatter: ({ value }) => formatReferenceCount(value),
			},
		],
	},
]);

// Product-grain colDefs, swapped in place of kpiDetailColumns while viewMode==='product' (RG-40: same
// <AgGrid> instance, no new prop/remount — see activeDetailColumns/activeDetailRows).
const defaultProductFactVisibleColumns = computed(() => ['companyName', 'label', 'value', 'difference', 'referenceCount', 'totalReferencesCount', ...cappedCategoryLevelFields('productTreeLevel'), ...cappedCategoryLevelFields('productCategoryLevel')]);
function isProductFactColumnHidden(colId) {
	return !defaultProductFactVisibleColumns.value.includes(colId);
}
function productStatementStateLabel(state) {
	if (state === 1) return t('Present');
	if (state === 2) return t('Absent');
	if (state === 3) return t('OutOfStock');
	return '—';
}
const kpiProductFactColumns = computed(() => [
	{
		colId: GRID_GROUP_COLUMNS.DELIVERY_LOCATION,
		field: 'deliveryLocation',
		headerName: t('DeliveryLocation'),
		hide: true,
		enableRowGroup: true,
		rowGroup: true,
		rowGroupIndex: 0,
		suppressColumnsToolPanel: true,
		valueGetter: ({ data }) => data?.deliveryLocation || data?.companyName || '',
	},
	{
		colId: GRID_GROUP_COLUMNS.ACCOUNT,
		field: 'account',
		headerName: t('Account'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.CHANNEL,
		field: 'channel',
		headerName: t('Channel'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.LOCATION_GROUP,
		field: 'locationGroup',
		headerName: t('Group'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.SPECIFICITIES,
		field: 'specificities',
		headerName: t('Specificities'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
	},
	{
		colId: GRID_GROUP_COLUMNS.PRODUCT,
		field: 'label',
		headerName: t('Product'),
		hide: true,
		enableRowGroup: true,
		suppressColumnsToolPanel: true,
		// Two different products can share a display label — key on productId so they group separately;
		// the group row still displays the plain label via this column's own field resolution.
		keyCreator: (params) => `${params.data?.productId ?? ''}|${params.value ?? ''}`,
	},
	...categoryRowGroupColumns.value,
	...productCategoryRowGroupColumns.value,
	...organisationRowGroupColumns.value,
	{
		headerName: t('DeliveryLocation'),
		groupId: 'deliveryLocation',
		openByDefault: false,
		marryChildren: true,
		children: [
			{
				field: 'companyName',
				headerName: t('CompanyName'),
				sortable: true,
				filter: true,
				hide: isProductFactColumnHidden('companyName'),
				resizable: true,
				minWidth: isTouchMode.value ? 150 : 220,
				cellRenderer: CompanyNameRenderer,
			},
			{ field: 'parentAccount', headerName: t('ParentAccount'), sortable: true, filter: true, hide: true, resizable: true, minWidth: 160 },
			{ field: 'account', headerName: t('Account'), sortable: true, filter: true, hide: isProductFactColumnHidden('account'), resizable: true, minWidth: 160 },
			{ field: 'channel', headerName: t('Channel'), sortable: true, filter: true, hide: isProductFactColumnHidden('channel'), resizable: true, minWidth: 140, columnGroupShow: 'open' },
			{ field: 'deliveryLocationType', headerName: t('DeliveryLocationType'), sortable: true, filter: true, hide: isProductFactColumnHidden('deliveryLocationType'), resizable: true, minWidth: 160, columnGroupShow: 'open' },
			{ field: 'address', headerName: t('Address'), sortable: true, filter: true, hide: isProductFactColumnHidden('address'), resizable: true, minWidth: 220, columnGroupShow: 'open' },
			{ field: 'postcode', headerName: t('Post Code'), sortable: true, filter: true, hide: isProductFactColumnHidden('postcode'), resizable: true, minWidth: 120, columnGroupShow: 'open' },
			{ field: 'city', headerName: t('City'), sortable: true, filter: true, hide: isProductFactColumnHidden('city'), resizable: true, minWidth: 140, columnGroupShow: 'open' },
			{ field: 'posType', headerName: t('PosType'), sortable: true, filter: true, hide: isProductFactColumnHidden('posType'), resizable: true, minWidth: 140, columnGroupShow: 'open' },
			{ field: 'locationGroup', headerName: t('Group'), sortable: true, filter: true, hide: isProductFactColumnHidden('locationGroup'), resizable: true, minWidth: 140, columnGroupShow: 'open' },
			{ field: 'distributionChannel', headerName: t('DistributionChannel'), sortable: true, filter: true, hide: isProductFactColumnHidden('distributionChannel'), resizable: true, minWidth: 160, columnGroupShow: 'open' },
			{ field: 'seasonality', headerName: t('DeliveryLocationSeasonality'), sortable: true, filter: true, hide: isProductFactColumnHidden('seasonality'), resizable: true, minWidth: 160, columnGroupShow: 'open' },
			...deliveryLocationAttributeColumns.value,
		],
	},
	{
		field: 'organisations',
		headerName: t('Organisation'),
		sortable: true,
		filter: true,
		hide: true,
		resizable: true,
		width: 180,
		children: Array.from({ length: organisationMaxLevel.value || 0 }, (_, index) => ({
			field: `organisationLevel${index + 1}`,
			headerName: `${t('Level')} ${index + 1}`,
			sortable: true,
			filter: true,
			resizable: true,
			hide: isProductFactColumnHidden(`organisationLevel${index + 1}`),
			width: 180,
			columnGroupShow: index === 0 ? null : 'open',
		})),
	},
	{
		headerName: t('Product'),
		groupId: 'product',
		openByDefault: false,
		marryChildren: true,
		children: [
			{ field: 'label', headerName: t('Product'), sortable: true, filter: true, hide: isProductFactColumnHidden('label'), resizable: true, minWidth: 200 },
			{ field: 'reference', headerName: t('Reference'), sortable: true, filter: true, hide: isProductFactColumnHidden('reference'), resizable: true, minWidth: 140, columnGroupShow: 'open' },
			{ field: 'ean', headerName: t('Ean'), sortable: true, filter: true, hide: isProductFactColumnHidden('ean'), resizable: true, minWidth: 140, columnGroupShow: 'open' },
			{ field: 'state', headerName: t('Status'), sortable: true, filter: true, hide: isProductFactColumnHidden('state'), resizable: true, minWidth: 140, columnGroupShow: 'open', valueFormatter: ({ value }) => productStatementStateLabel(value) },
		],
	},
	{
		field: 'value',
		headerName: detailTableTitle.value,
		sortable: true,
		filter: true,
		resizable: true,
		minWidth: isTouchMode.value ? 150 : 240,
		cellDataType: 'number',
		cellRenderer: ProgressBarRenderer,
		cellRendererParams: () => ({
			formatMetric: formatKpiMetric,
			objectiveLabel: t('Objective'),
			needLabel: false,
			isPercentage: isDetailPercentage.value,
			bufferColor: 'grey-lighten-1',
			bufferOpacity: 0.35,
		}),
		valueFormatter: ({ value }) => formatKpiMetric(value),
		sort: 'desc',
	},
	{ field: 'objective', headerName: t('Objective'), sortable: true, filter: true, hide: isProductFactColumnHidden('objective'), resizable: true, maxWidth: 200, cellDataType: 'number', enableValue: true, valueFormatter: ({ value }) => formatKpiMetric(value) },
	{ field: 'difference', headerName: t('Difference'), sortable: true, filter: true, hide: isProductFactColumnHidden('difference'), resizable: true, maxWidth: 200, cellDataType: 'number', enableValue: true, valueFormatter: ({ value }) => formatKpiMetric(value, true), cellStyle: ({ value }) => getDifferenceCellStyle(value) },
	{
		headerName: t('StatementCategories'),
		groupId: 'categories',
		openByDefault: false,
		marryChildren: true,
		hide: true,
		children: Array.from({ length: cappedCategoryLevel.value || 0 }, (_, index) => ({
			field: `productTreeLevel${index + 1}`,
			headerName: `${t('StatementCategoryLevel')} ${index + 1}`,
			sortable: true,
			filter: true,
			resizable: true,
			hide: isProductFactColumnHidden(`productTreeLevel${index + 1}`),
			minWidth: 180,
			columnGroupShow: null,
		})),
	},
	{
		headerName: t('ProductCategory'),
		groupId: 'productCategories',
		openByDefault: false,
		marryChildren: true,
		hide: true,
		children: Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => ({
			field: `productCategoryLevel${index + 1}`,
			headerName: `${t('ProductCategory')} ${index + 1}`,
			sortable: true,
			filter: true,
			resizable: true,
			hide: isProductFactColumnHidden(`productCategoryLevel${index + 1}`),
			minWidth: 180,
			columnGroupShow: index + 1 <= (cappedCategoryLevel.value || 1) ? null : 'open',
		})),
	},
	{
		headerName: t('CodesCount'),
		groupId: 'codesCount',
		openByDefault: false,
		marryChildren: true,
		children: [
			{ field: 'referenceCount', headerName: t('ReferencesCount'), sortable: true, filter: true, hide: isProductFactColumnHidden('referenceCount'), resizable: true, maxWidth: 200, cellDataType: 'number', valueFormatter: ({ value }) => formatReferenceCount(value) },
			{ field: 'totalReferencesCount', headerName: t('TotalReferencesCount'), sortable: true, filter: true, hide: isProductFactColumnHidden('totalReferencesCount'), resizable: true, maxWidth: 220, cellDataType: 'number', valueFormatter: ({ value }) => formatReferenceCount(value) },
		],
	},
]);

// Maps ag-Grid row-group colIds to the underlying row fields so the mobile card list can group on the
// exact same columns as QuickRowGroup drives on the grid. Derived from the colDefs, never hardcoded.
const rowGroupFieldByColId = computed(() => {
	const mapping = {};
	const visit = (columnDefs) => {
		(Array.isArray(columnDefs) ? columnDefs : []).forEach((columnDef) => {
			if (!columnDef || typeof columnDef !== 'object') {
				return;
			}
			if (Array.isArray(columnDef.children) && columnDef.children.length > 0) {
				visit(columnDef.children);
				return;
			}
			if (typeof columnDef.colId === 'string' && columnDef.colId.startsWith('rowGroup') && columnDef.field) {
				mapping[columnDef.colId] = columnDef.field;
			}
		});
	};

	visit(kpiDetailColumns.value);
	// Mirrors the delivery-location valueGetter, which falls back to the company name.
	mapping[GRID_GROUP_COLUMNS.DELIVERY_LOCATION] = ['deliveryLocation', 'companyName'];
	return mapping;
});

const touchDetailColumns = computed(() => [
	{ field: 'value', label: detailTableTitle.value, type: 'progress', objectiveField: 'objective', colorField: 'color', classColorField: 'classColor', isPercentage: isDetailPercentage.value, needObjectiveLabel: true, objectiveLabel: t('Objective'), formatMetric: (value) => formatKpiMetric(value), deltaField: 'difference', deltaFormatMetric: (value) => formatKpiMetric(value, true) },
	{ field: 'referenceCount', label: t('ReferencesCount'), formatMetric: formatReferenceCount, detail: true },
	{ field: 'totalReferencesCount', label: t('TotalReferencesCount'), formatMetric: formatReferenceCount, detail: true },
]);

const filteredKpiDetailRows = computed(() => {
	let rows = kpiDetailRows.value;

	if (isOrgGroupingActive.value) {
		rows = rows.flatMap((row) => {
			if (!Array.isArray(row._organisationPaths)) {
				return [row];
			}
			return row._organisationPaths.map((orgLevels, index) => ({
				...row,
				...orgLevels,
				id: index === 0 ? row.id : `${row.id}__org${index}`,
			}));
		});
	}

	const searchValue = kpiDetailSearch.value?.trim().toLowerCase();
	if (!searchValue) {
		return rows;
	}

	return rows.filter((row) => {
		const categoryValues = Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => row[`productTreeLevel${index + 1}`]);
		const dynamicAttributeValues = deliveryLocationAttributeColumns.value.map((column) => row[column.field]);
		const searchableValues = [row.companyName, row.deliveryLocation, row.parentAccount, row.account, row.channel, row.deliveryLocationType, row.address, row.postcode, row.city, row.posType, row.locationGroup, row.distributionChannel, row.seasonality, row.organisations, row.reference, row.productTree, row.specificities, ...categoryValues, ...dynamicAttributeValues];
		return searchableValues.some((value) =>
			String(value || '')
				.toLowerCase()
				.includes(searchValue)
		);
	});
});

const isProductOrgGroupingActive = computed(() => selectedProductGridRowGroups.value.some((colId) => colId.startsWith('rowGroupOrganisationLevel')));
const filteredProductDetailRows = computed(() => {
	let rows = kpiProductFactRows.value;

	if (isProductOrgGroupingActive.value) {
		rows = rows.flatMap((row) => {
			if (!Array.isArray(row._organisationPaths)) {
				return [row];
			}
			return row._organisationPaths.map((orgLevels, index) => ({
				...row,
				...orgLevels,
				id: index === 0 ? row.id : `${row.id}__org${index}`,
			}));
		});
	}

	const searchValue = kpiDetailSearch.value?.trim().toLowerCase();
	if (!searchValue) {
		return rows;
	}

	return rows.filter((row) => {
		const productCategoryValues = Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => row[`productCategoryLevel${index + 1}`]);
		const categoryValues = Array.from({ length: cappedCategoryLevel.value || 0 }, (_, index) => row[`productTreeLevel${index + 1}`]);
		const dynamicAttributeValues = deliveryLocationAttributeColumns.value.map((column) => row[column.field]);
		const searchableValues = [row.companyName, row.deliveryLocation, row.account, row.channel, row.deliveryLocationType, row.address, row.postcode, row.city, row.locationGroup, row.organisations, row.label, row.reference, row.ean, row.productCategory, row.specificities, ...productCategoryValues, ...categoryValues, ...dynamicAttributeValues];
		return searchableValues.some((value) =>
			String(value || '')
				.toLowerCase()
				.includes(searchValue)
		);
	});
});

// Single grid instance, swapped in place (RG-40) — no new AgGrid prop/key needed, its existing
// watch(rowData)/watch(colDefs) already re-applies on prop change.
const activeDetailRows = computed(() => (viewMode.value === 'product' ? filteredProductDetailRows.value : filteredKpiDetailRows.value));
const activeDetailColumns = computed(() => (viewMode.value === 'product' ? kpiProductFactColumns.value : kpiDetailColumns.value));
const activeQuickRowGroups = computed(() => (viewMode.value === 'product' ? productQuickRowGroupDefinitions.value : quickRowGroupDefinitions.value));
const activeGroupRowAgg = computed(() => (viewMode.value === 'product' ? getKpiProductGrainGroupRowAgg : getKpiDetailGroupRowAgg));

// RG-20: laptop-only (touch/mobile excluded, matching AgGrid's own !isLaptop gate below — a tablet
// narrower than that already gets the card UI, before any tablet-landscape carve-in could ever apply)
// plus ShareOfShelf excluded (its denominator can't decompose to product grain — see plan décisions).
const canShowProductGrainToggle = computed(() => !isLaptop.value && !hasSameReference(activeKpiReference.value, 'ShareOfShelf'));

const organisationMaxLevel = ref(null);
const isOrgGroupingActive = computed(() => selectedGridRowGroups.value.some((colId) => colId.startsWith('rowGroupOrganisationLevel')));

onMounted(() => {
	eventBus.on('custom-kpi-removed', onCustomKpiRemoved);
	eventBus.on('kpi-simulated-changed', onKpiSimulatedChanged);
	init().finally(() => {
		isPageLoading.value = false;
	});

	window.addEventListener('resize', onResize);
});

onBeforeUnmount(() => {
	eventBus.off('custom-kpi-removed', onCustomKpiRemoved);
	eventBus.off('kpi-simulated-changed', onKpiSimulatedChanged);

	window.removeEventListener('resize', onResize);
});

watch(error, (newVal) => {
	if (newVal) {
		toast(newVal, { style: { background: 'red' } });
	}
});

watch(
	() => getKpiDetailLoadKey(buildFiltersFromParams(props.params)),
	async () => {
		const newParams = props.params || {};
		activeKpiReference.value = newParams?.reference || '';
		isDetailSimulated.value = Boolean(newParams?.simulated);

		if ('allPark' in newParams) {
			allPark.value = Boolean(newParams.allPark);
		}

		if (!kpis.value.length) {
			return;
		}

		await syncFiltersFromParams(newParams);
		if (showKpiDetailGrid.value) {
			const filters = buildFiltersFromParams(newParams);
			if (filters) filters.allPark = allPark.value;
			await loadKpiDetails(filters);
		}
	}
);

watch(
	[selectedGridRowGroups, selectedProductGridRowGroups],
	() => {
		applyDetailRowGrouping();
		bumpViewStateVersion();
	},
	{ deep: false }
);

function pruneSelectedGroupsToAvailable(groups, selectedGroups) {
	const availableColumns = new Set(groups.flatMap((group) => (Array.isArray(group.levels) ? group.levels.map((level) => level.columnField) : [group.columnField])).filter(Boolean));
	const nextSelectedGroups = selectedGroups.value.filter((columnField) => availableColumns.has(columnField));
	if (nextSelectedGroups.length !== selectedGroups.value.length) {
		selectedGroups.value = nextSelectedGroups;
	}
}

watch(quickRowGroupDefinitions, (groups) => pruneSelectedGroupsToAvailable(groups, selectedGridRowGroups), { deep: false, immediate: true });
watch(productQuickRowGroupDefinitions, (groups) => pruneSelectedGroupsToAvailable(groups, selectedProductGridRowGroups), { deep: false, immediate: true });

watch(
	() => activeDetailColumns.value,
	async () => {
		await nextTick();
		applyDetailRowGrouping();
	},
	{ deep: false }
);

// A saved product-grain view opened somewhere the breakpoint no longer qualifies (RG-20) falls back to
// store view silently, same as the cas-limite for an unsupported device.
watch(canShowProductGrainToggle, (canShow) => {
	if (!canShow && viewMode.value === 'product') {
		viewMode.value = 'store';
	}
});

// The switch now sits in the header, outside the panel/sheet, so a user toggle applies on its own. The
// isFilterSheetOpen guard stays for the panel's own Reset button, which flips this switch while open and
// owns the reload itself (see resetFilters).
watch(allPark, async (newVal, oldVal) => {
	if (isApplyingViewState || newVal === oldVal || !hasAppliedFilters.value || isFilterSheetOpen.value) return;
	await applyFilters({ skipPersistGeneratedCustomKpi: true });
});

// Crossing the breakpoint remounts the filter controls (panel <-> bottom sheet), so push the
// currently applied filters back into the fresh instances.
watch(isLaptop, async () => {
	await syncFiltersFromParams(currentAppliedFilters.value || props.params);
});

// The card list has no ag-Grid API to report its displayed rows.
watch(
	[filteredKpiDetailRows, isLaptop],
	() => {
		if (!isLaptop.value) return;
		displayedDeliveryLocationCount.value = getDisplayedUniqueDeliveryLocationCount();
	},
	{ immediate: true }
);

async function init() {
	isUserViewReady.value = false;
	await getFiltersData();
	await getRowGroupData();
	await loadPersistedCustomKpis();
	await syncFiltersFromParams(props.params);
	hasAppliedFilters.value = !!(activeKpiReference.value || props.params?.reference);
	await nextTick();
	await waitForDetailGridReady();
	isUserViewReady.value = true;
}

async function onFiltersApply() {
	isFilterSheetOpen.value = false;
	await applyFilters();
}

// Never overwrites a live instance with the null Vue passes on unmount — same reason FilterControls
// guards its own setControl(), though the header select is mounted once and never swapped.
function setKpiReferenceControl(instance) {
	if (instance) {
		kpiReferenceSelect.value = instance;
	}
}

// The select sits outside the panel that owns the Apply button, so picking a KPI has to apply on its own
// — same immediate-apply contract as the AllPark switch beside it, and the same call the panel Apply makes
// (persisted generated custom KPI included). Every programmatic set (syncFiltersFromParams,
// applyUserViewState, SelectControl's own preSelected watch) lands on the already-active reference, so the
// reference check below no-ops them all.
async function onKpiReferenceChanged() {
	if (isApplyingViewState) {
		return;
	}

	const selectedKpi = kpiReferenceSelect.value?.getFullValues();
	if (!selectedKpi?.reference || hasSameReference(selectedKpi.reference, activeKpiReference.value)) {
		return;
	}

	await applyFilters();
}

async function getFiltersData() {
	kpis.value = kpiService.getAvailableKpis(true);
	organisations.value = await getOrganisationsTree();
	deliveryLocationTypes.value = typeof ecosVM?.deliveryLocationTypes === 'function' ? ecosVM.deliveryLocationTypes() : ecosVM?.deliveryLocationTypes || [];
	accounts.value = await getAccounts();
	channels.value = await getChannels();
	const productTreeTreeItems = await getProductTreesTree();
	productTrees.value = Array.isArray(productTreeTreeItems) ? productTreeTreeItems : [];
	const attributes = await getAttributes(false, 'tokenSales');
	attributesById.value = new Map((Array.isArray(attributes) ? attributes : []).map((attribute) => [Number(attribute.id), attribute]));
	const specificityAttribute = await getAttributeById(69, 'tokenAssort');
	specificities.value = specificityAttribute ? specificityAttribute.values : [];
	companies.value = await getCompanies();
}

async function getRowGroupData() {
	organisationMaxLevel.value = await getOrganisationMaxLevel();
	statementCategoryLevel.value = kpiService.getStatementCategoryLevel();
}

async function loadPersistedCustomKpis() {
	userSettings.value = await getUserSettings({ groupName: 'WEBAPP', userId: user.id });
	const customKpiSettings = userSettings.value.find((setting) => setting.name === KPI_BUILDER_CUSTOMS_SETTING_NAME);
	localKpiComponents.value = parsePersistedCustomKpis(customKpiSettings?.value);
}

function parsePersistedCustomKpis(settingValue) {
	if (!settingValue) {
		return [];
	}

	try {
		const parsedValue = JSON.parse(settingValue);
		if (!Array.isArray(parsedValue)) {
			return [];
		}

		return parsedValue.map((customKpi) => mapStoredCustomKpi(customKpi)).filter(Boolean);
	} catch {
		error.value = 'Impossible de charger les KPIs personnalises enregistres.';
		return [];
	}
}

function buildFiltersFromParams(params = {}) {
	const reference = params.reference || activeKpiReference.value;
	if (!reference) {
		return null;
	}

	const filters = {
		reference,
		accounts: params.accounts || [],
		channels: params.channels || [],
		clusterId: params.clusterId ?? null,
		dlts: params.dlts || params.deliveryLocationTypes || [],
		// productTrees/pts is the legacy name of the STATEMENT axis (widget params, BO custom KPIs, views
		// saved before the split all carry it with that meaning), so it keeps feeding that field.
		statementProductTrees: params.statementProductTrees || params.productTrees || params.pts || [],
		productCategories: params.productCategories || [],
		specificities: params.specificities || params.spes || [],
		organisations: params.organisations || [],
		companies: params.companies || [],
		labels: params.labels || [],
		eans: params.eans || [],
		references: params.references || [],
		excludeSharedOrganisations: Boolean(params.excludeSharedOrganisations),
	};

	const simulatedValue = getSimulatedFilterValue(reference, params);
	if (simulatedValue !== undefined) {
		filters.simulated = simulatedValue;
	}

	filters.allPark = params.allPark ?? false;

	return filters;
}

function getSimulatedFilterValue(reference, params = {}) {
	if ('simulated' in params) {
		return Boolean(params.simulated);
	}

	if (reference && hasSameReference(reference, activeKpiReference.value) && 'simulated' in mainKpiParams.value) {
		return Boolean(isDetailSimulated.value);
	}

	if (reference && hasSameReference(reference, props.params?.reference) && 'simulated' in (props.params || {})) {
		return Boolean(props.params.simulated);
	}

	if (reference && hasSameReference(reference, mainKpiParams.value?.reference) && 'simulated' in mainKpiParams.value) {
		return Boolean(mainKpiParams.value.simulated);
	}

	return undefined;
}

async function syncFiltersFromParams(params = {}) {
	await nextTick();

	const selectedKpi = findKpiByReference(params.reference || activeKpiReference.value);
	if (selectedKpi && kpiReferenceSelect.value?.selectItems) {
		kpiReferenceSelect.value.selectItems(selectedKpi._uid);
	}

	organisationTreeview.value?.selectItems(params.organisations || []);
	deliveryLocationTypeSelect.value?.selectItems(params.dlts || params.deliveryLocationTypes || []);
	accountSelect.value?.selectItems(params.accounts || []);
	channelSelect.value?.selectItems(params.channels || []);
	statementProductTreeTreeview.value?.selectItems(params.statementProductTrees || params.productTrees || params.pts || []);
	productTreeTreeview.value?.selectItems(params.productCategories || []);
	specificitySelect.value?.selectItems(params.specificities || params.spes || []);
	companySelect.value?.selectItems(params.companies || []);
	labelTokens.value?.selectItems(params.labels || []);
	eanTokens.value?.selectItems(params.eans || []);
	referenceTokens.value?.selectItems(params.references || []);
	excludeSharedOrganisationsToggle.value?.selectItems(Boolean(params.excludeSharedOrganisations));

	updateSelectedFilterChips();
	filterControls.value?.refreshActiveCount();
}

function buildFiltersFromControls(selectedKpi = null) {
	const kpiValue = selectedKpi || kpiReferenceSelect.value?.getFullValues() || findKpiByReference(activeKpiReference.value);
	if (!kpiValue?.reference) {
		return null;
	}

	const filters = {
		reference: kpiValue.reference,
		accounts: accountSelect.value?.getValues() || [],
		channels: channelSelect.value?.getValues() || [],
		dlts: deliveryLocationTypeSelect.value?.getValues() || [],
		statementProductTrees: statementProductTreeTreeview.value?.getValues() || [],
		productCategories: productTreeTreeview.value?.getValues() || [],
		specificities: specificitySelect.value?.getValues() || [],
		organisations: organisationTreeview.value?.getValues() || [],
		companies: companySelect.value?.getValues() || [],
		labels: labelTokens.value?.getValues() || [],
		eans: eanTokens.value?.getValues() || [],
		references: referenceTokens.value?.getValues() || [],
		excludeSharedOrganisations: Boolean(excludeSharedOrganisationsToggle.value?.getValues()),
	};

	const widgetParams = mainWidget.value?.params;
	if (widgetParams && 'simulated' in widgetParams) {
		filters.simulated = Boolean(isDetailSimulated.value);
	}

	filters.allPark = allPark.value;

	return filters;
}

async function loadKpiDetails(filters, options = {}) {
	const loadKey = getKpiDetailLoadKey(filters);
	if (loadKey && loadKey === latestKpiDetailLoadKey && !kpiDetailError.value && !isKpiDetailLoading.value && options?.forceReload !== true) {
		return;
	}

	// A real filter/KPI-reference change resets to store view rather than trying to keep product-grain rows
	// live-synced — the product toggle is a deliberate action (RG-19), not an auto-recompute. Bumping
	// productFactLoadRequestId invalidates any toggleProductGrain() call still in flight, so a slow count/
	// build that resolves after this point can never overwrite these resets with stale-filter data.
	viewMode.value = 'store';
	kpiProductFactRows.value = [];
	productFactError.value = null;
	isProductFactLoading.value = false;
	++productFactLoadRequestId;

	const requestId = ++kpiDetailLoadRequestId;
	const previousGridState = options?.preserveGridState === false ? null : getDetailGridViewState();

	if (!filters?.reference) {
		kpiDetailRows.value = [];
		kpiDetailError.value = null;
		displayedDeliveryLocationCount.value = 0;
		isDetailSimulated.value = false;
		return;
	}

	try {
		isKpiDetailLoading.value = true;
		await nextTick();
		await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
		await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
		await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
		kpiDetailError.value = null;
		activeKpiReference.value = filters.reference;
		currentAppliedFilters.value = { ...filters };
		isDetailSimulated.value = Boolean(filters.simulated);
		const rows = await kpiService.getKpiDetailRows(filters.reference, filters);
		const normalizedRows = (Array.isArray(rows) ? rows : []).map((row) => ({
			...row,
			totalReferencesCount: Number(row?.totalReferencesCount ?? 0),
		}));
		const { rows: enrichedRows, columns } = await enrichRowsWithDeliveryLocationAttributes(normalizedRows);
		if (requestId !== kpiDetailLoadRequestId) {
			return;
		}

		kpiDetailRows.value = enrichedRows;
		deliveryLocationAttributeColumns.value = columns;
		latestKpiDetailLoadKey = loadKey;
	} catch (err) {
		if (requestId !== kpiDetailLoadRequestId) {
			return;
		}

		kpiDetailRows.value = [];
		deliveryLocationAttributeColumns.value = [];
		displayedDeliveryLocationCount.value = 0;
		kpiDetailError.value = err.message;
	} finally {
		if (requestId === kpiDetailLoadRequestId) {
			isKpiDetailLoading.value = false;
			if (previousGridState && options?.preserveGridState !== false) {
				await nextTick();
				applyDetailGridViewState(previousGridState, { preserveRowGroups: true });
				applyDetailRowGrouping();
			}
		}
	}
}

// RG-19: a deliberate action, never an auto-recompute. Blocked while any load is in-flight (RG-39);
// toggling back to store view is instant (no reload — the data is already there).
async function toggleProductGrain() {
	if (isKpiDetailLoading.value || isProductFactLoading.value) {
		return;
	}

	if (viewMode.value === 'product') {
		viewMode.value = 'store';
		return;
	}

	const filters = currentAppliedFilters.value;
	if (!filters?.reference) {
		return;
	}

	// A filter/KPI change mid-toggle (loadKpiDetails) bumps this to invalidate whichever of the two awaits
	// below is still in flight — without this, a slow count/build resolving after the user has already
	// moved on to different filters would silently apply product-grain rows computed from the old ones.
	const requestId = ++productFactLoadRequestId;
	isProductFactLoading.value = true;
	productFactError.value = null;
	try {
		// Count first (cheap: skips identity/metric/org-path work) — stands in for the Jetsam protection a
		// server-side block-fetch would have given, now that the feature is CSRM/in-memory (see plan).
		const count = await kpiService.getKpiProductFactRows(filters.reference, filters, { countOnly: true });
		if (requestId !== productFactLoadRequestId) {
			return;
		}
		if (count > PRODUCT_GRAIN_VOLUMETRY_THRESHOLD) {
			error.value = t('ProductGrainTooManyRows');
			return;
		}

		const rows = await kpiService.getKpiProductFactRows(filters.reference, filters);
		if (requestId !== productFactLoadRequestId) {
			return;
		}

		kpiProductFactRows.value = Array.isArray(rows) ? rows : [];
		if (!selectedProductGridRowGroups.value.length) {
			selectedProductGridRowGroups.value = [...defaultProductGridRowGroups.value];
		}
		viewMode.value = 'product';
		await nextTick();
		applyDetailRowGrouping();
	} catch (err) {
		if (requestId !== productFactLoadRequestId) {
			return;
		}
		productFactError.value = err.message;
		error.value = err.message;
	} finally {
		if (requestId === productFactLoadRequestId) {
			isProductFactLoading.value = false;
		}
	}
}

// toggleProductGrain() is a TOGGLE, not a setter: calling it while product grain is already on turns it
// back OFF. loadKpiDetails() early-returns without resetting anything when the load key is unchanged,
// so the guard has to be on the CURRENT viewMode, not only on the wanted one.
async function ensureProductGrain() {
	if (viewMode.value === 'product' || !canShowProductGrainToggle.value) {
		return;
	}

	await toggleProductGrain();
}

// Changing FILTERS is not changing the axis: adding one, dropping one chip, clearing them all or flipping
// AllPark must all leave the Produit/Lieu-de-livraison grain where the user put it (RG-19 makes the
// grain a deliberate choice, so losing it on a filter tweak contradicts it as much as auto-entering it
// would). Only a measure change still resets, through loadKpiDetails() directly. Two things have to be
// put back, not one: loadKpiDetails() resets the grain, AND the column state it restores by itself is
// captured on the product columns but re-applied after the swap to store grain, so AgGrid drops every
// product-only column from it — which is exactly how a manually shown EAN column went missing.
async function reloadDetailsPreservingProductGrain(filters) {
	const previousViewMode = viewMode.value;
	const previousGridState = previousViewMode === 'product' ? getDetailGridViewState() : null;

	await loadKpiDetails(filters);

	if (previousViewMode !== 'product') {
		return;
	}

	await ensureProductGrain();
	if (viewMode.value !== 'product' || !previousGridState) {
		return;
	}

	await nextTick();
	applyDetailGridViewState(previousGridState, { preserveRowGroups: true });
	applyDetailRowGrouping();
}

function getKpiDetailLoadKey(filters) {
	if (!filters?.reference) {
		return '';
	}

	const normalizeValue = (value) => {
		if (Array.isArray(value)) {
			return [...value].sort((left, right) => String(left).localeCompare(String(right)));
		}

		if (value && typeof value === 'object') {
			return Object.keys(value)
				.sort((left, right) => left.localeCompare(right))
				.reduce((normalized, key) => {
					normalized[key] = normalizeValue(value[key]);
					return normalized;
				}, {});
		}

		return value;
	};

	return JSON.stringify(normalizeValue(filters));
}

function applyDetailRowGrouping() {
	if (!kpiDetailGrid.value || typeof kpiDetailGrid.value.setRowGroupColumns !== 'function') {
		return;
	}

	const api = typeof kpiDetailGrid.value.getGridApi === 'function' ? kpiDetailGrid.value.getGridApi() : null;
	const currentGroups =
		typeof api?.getRowGroupColumns === 'function'
			? api
					.getRowGroupColumns()
					.map((column) => column?.getColId?.())
					.filter(Boolean)
			: [];
	// Reads whichever quick-group array matches the CURRENT viewMode — a toggle must not try to apply the
	// other grain's colIds (they don't exist on this grain's rows).
	const targetGroups = viewMode.value === 'product' ? selectedProductGridRowGroups.value : selectedGridRowGroups.value;
	if (haveSameItems(currentGroups, targetGroups)) {
		return;
	}

	kpiDetailGrid.value.setRowGroupColumns(targetGroups);
}

function bumpViewStateVersion() {
	viewStateVersion.value += 1;
}

function onKpiDetailGridReady() {
	isKpiDetailGridReady.value = true;
	while (detailGridReadyResolvers.length > 0) {
		detailGridReadyResolvers.shift()?.();
	}
}

function onDetailGridViewStateChanged() {
	bumpViewStateVersion();
}

function getDetailGridViewState() {
	const gridState = typeof kpiDetailGrid.value?.getViewState === 'function' ? kpiDetailGrid.value.getViewState() : null;
	if (gridState) {
		lastKnownGridState.value = gridState;
	}
	return gridState;
}

function applyDetailGridViewState(gridState, options = {}) {
	if (!gridState || typeof kpiDetailGrid.value?.applyViewState !== 'function') {
		return;
	}

	kpiDetailGrid.value.applyViewState(gridState, options);

	// A persisted/previous columnState can predate companyName joining the default-visible set (both
	// grains), carrying a stale hide:true for it — never let that win over the current default.
	kpiDetailGrid.value.getGridApi?.()?.setColumnsVisible?.(['companyName'], true);
}

async function waitForDetailGridReady(timeoutMs = 2000) {
	// The grid is not rendered on mobile, so waiting would just burn the whole timeout during init().
	if (isLaptop.value) {
		return true;
	}

	if (isKpiDetailGridReady.value && typeof kpiDetailGrid.value?.getGridApi === 'function' && kpiDetailGrid.value.getGridApi()) {
		return true;
	}

	return await new Promise((resolve) => {
		const timeoutId = setTimeout(() => {
			const resolverIndex = detailGridReadyResolvers.indexOf(onReady);
			if (resolverIndex >= 0) {
				detailGridReadyResolvers.splice(resolverIndex, 1);
			}
			resolve(false);
		}, timeoutMs);

		const onReady = () => {
			clearTimeout(timeoutId);
			resolve(true);
		};

		detailGridReadyResolvers.push(onReady);
	});
}

function normalizeRowGroupColumns(colIds = [], definitions = quickRowGroupDefinitions.value) {
	const availableColumns = new Set(definitions.flatMap((group) => (Array.isArray(group.levels) ? group.levels.map((level) => level.columnField) : [group.columnField])).filter((columnField) => typeof columnField === 'string' && columnField.length > 0));

	return [...new Set((Array.isArray(colIds) ? colIds : []).filter((colId) => typeof colId === 'string' && availableColumns.has(colId)))];
}

function getUserViewState() {
	const filters = buildFiltersFromControls() || buildFiltersFromParams(mainKpiParams.value);
	if (!filters?.reference) {
		return null;
	}

	// On mobile the grid is not mounted: fall back to the last state we saw (or the one the applied view
	// carried) so saving a view from a phone never wipes its desktop column layout.
	const gridState = getDetailGridViewState() || lastKnownGridState.value;

	return {
		filters: getUserViewFilters(filters),
		quickGroups: [...selectedGridRowGroups.value],
		productQuickGroups: [...selectedProductGridRowGroups.value],
		viewMode: viewMode.value,
		showAdditionalFilters: showAdditionalFilters.value,
		allPark: allPark.value,
		showKpiDetailGrid: showKpiDetailGrid.value,
		gridState,
	};
}

function getDefaultUserViewState() {
	const filters = buildFiltersFromControls() || buildFiltersFromParams(mainKpiParams.value);
	if (!filters?.reference) {
		return null;
	}

	return {
		filters: getUserViewFilters(filters),
		quickGroups: [...defaultDetailGridRowGroups.value],
		productQuickGroups: [...defaultProductGridRowGroups.value],
		viewMode: 'store',
		showAdditionalFilters: false,
		showKpiDetailGrid: false,
		gridState: buildDefaultDetailGridState(),
	};
}

async function applyUserViewState(viewState = {}) {
	const targetReference = getCurrentKpiReference(viewState?.filters?.reference);
	const filters = buildFiltersFromParams({
		...(viewState?.filters || {}),
		reference: targetReference,
	});
	if (!filters?.reference) {
		throw new Error('Invalid user view state');
	}

	isApplyingViewState = true;
	try {
		error.value = null;
		hasAppliedFilters.value = true;
		activeKpiReference.value = filters.reference;
		allPark.value = Boolean(viewState?.allPark ?? true);
		showKpiDetailGrid.value = Boolean(viewState?.showKpiDetailGrid ?? false);
		showAdditionalFilters.value = Boolean(viewState?.showAdditionalFilters ?? false);
		if (viewState?.gridState) {
			lastKnownGridState.value = viewState.gridState;
		}

		await syncFiltersFromParams(filters);
		selectedGridRowGroups.value = normalizeRowGroupColumns(Array.isArray(viewState?.quickGroups) ? viewState.quickGroups : defaultDetailGridRowGroups.value);
		selectedProductGridRowGroups.value = normalizeRowGroupColumns(Array.isArray(viewState?.productQuickGroups) ? viewState.productQuickGroups : defaultProductGridRowGroups.value, productQuickRowGroupDefinitions.value);

		if (showKpiDetailGrid.value) {
			await loadKpiDetails(filters, { preserveGridState: false });
			await nextTick();
			await waitForDetailGridReady();

			// loadKpiDetails always resets to store view; restore product grain here, after the reload, and
			// only when the breakpoint/volumetry still allow it — otherwise fall back silently to store view
			// (matches the cas-limite for a saved Produit-axis view opened on an unsupported device).
			if (viewState?.viewMode === 'product') {
				await ensureProductGrain();
			}

			applyDetailRowGrouping();
			await nextTick();
			applyDetailGridViewState(viewState?.gridState, { preserveRowGroups: true });
		}

		updateSelectedFilterChips();
		bumpViewStateVersion();
	} finally {
		isApplyingViewState = false;
	}
}

function buildDefaultDetailGridState() {
	return {
		columnState: buildDefaultDetailGridColumnState(),
		filterModel: {},
		rowGroupColumns: [...defaultDetailGridRowGroups.value],
	};
}

function buildDefaultDetailGridColumnState() {
	const visibleColumnIds = new Set(defaultDetailGridVisibleColumns.value);
	const groupColumnIds = new Set(defaultDetailGridRowGroups.value);

	return getLeafColumnIds(kpiDetailColumns.value).map((colId) => ({
		colId,
		hide: !visibleColumnIds.has(colId),
		rowGroup: groupColumnIds.has(colId),
		rowGroupIndex: defaultDetailGridRowGroups.value.indexOf(colId) >= 0 ? defaultDetailGridRowGroups.value.indexOf(colId) : null,
	}));
}

function getLeafColumnIds(columnDefs = []) {
	return (Array.isArray(columnDefs) ? columnDefs : []).flatMap((columnDef) => {
		if (!columnDef || typeof columnDef !== 'object') {
			return [];
		}

		if (Array.isArray(columnDef.children) && columnDef.children.length > 0) {
			return getLeafColumnIds(columnDef.children);
		}

		const colId = columnDef.colId || columnDef.field;
		return typeof colId === 'string' && colId.length > 0 ? [colId] : [];
	});
}

function onUserViewApplied(view) {
	const isDefaultView = view?.id === USER_VIEW_DEFAULT_ID;
	defaultActiveGridRowGroups.value = isDefaultView ? [...defaultDetailGridRowGroups.value] : [];
	defaultActiveProductGridRowGroups.value = isDefaultView ? [...defaultProductGridRowGroups.value] : [];
}

function getCurrentKpiReference(fallbackReference = null) {
	return activeKpiReference.value || props.params?.reference || kpiReferenceSelect.value?.getFullValues()?.reference || fallbackReference || '';
}

function getUserViewFilters(filters = {}) {
	if (!filters || typeof filters !== 'object') {
		return {};
	}

	const { reference, ...userViewFilters } = filters;
	void reference;
	return userViewFilters;
}

function onDisplayedRowCountChanged(rowCount) {
	void rowCount;
	displayedDeliveryLocationCount.value = getDisplayedUniqueDeliveryLocationCount();
}

function getDisplayedUniqueDeliveryLocationCount() {
	const gridApi = typeof kpiDetailGrid.value?.getGridApi === 'function' ? kpiDetailGrid.value.getGridApi() : null;
	if (!gridApi || typeof gridApi.forEachNodeAfterFilter !== 'function') {
		return [...new Set((Array.isArray(filteredKpiDetailRows.value) ? filteredKpiDetailRows.value : []).map((row) => getDeliveryLocationIdFromRow(row)).filter(Boolean))].length;
	}

	const deliveryLocationIds = new Set();
	gridApi.forEachNodeAfterFilter((node) => {
		if (node?.group || !node?.data) {
			return;
		}

		const deliveryLocationId = getDeliveryLocationIdFromRow(node.data);
		if (deliveryLocationId) {
			deliveryLocationIds.add(deliveryLocationId);
		}
	});

	return deliveryLocationIds.size;
}

function onGridRowGroupColumnsChanged(colIds = []) {
	const normalizedColIds = [...new Set((Array.isArray(colIds) ? colIds : []).filter((colId) => typeof colId === 'string' && colId.length > 0))];
	if (haveSameItems(normalizedColIds, selectedGridRowGroups.value)) {
		return;
	}

	selectedGridRowGroups.value = normalizedColIds;
}

function haveSameItems(first = [], second = []) {
	if (first.length !== second.length) {
		return false;
	}

	const secondSet = new Set(second);
	return first.every((item) => secondSet.has(item));
}

function getDeliveryLocationIdFromRow(row) {
	const id = row?.deliveryLocationId ?? row?.id;
	const numericId = Number(id);
	return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function getDeliveryLocationIdFromAttributeItem(item) {
	const id = item?.deliveryLocationId ?? item?.id;
	const numericId = Number(id);
	return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function getAttributeId(attribute) {
	const id = attribute?.attributeId ?? attribute?.id;
	const numericId = Number(id);
	return Number.isInteger(numericId) && numericId > 0 ? numericId : null;
}

function getAttributeField(attributeId) {
	return `deliveryLocationAttribute_${attributeId}`;
}

function getAttributeColumnHeader(attributeId) {
	const attributeDefinition = attributesById.value.get(attributeId);
	return attributeDefinition?.name || `${t('Attribute')} ${attributeId}`;
}

function buildAttributeColumn(attributeId) {
	return {
		field: getAttributeField(attributeId),
		headerName: getAttributeColumnHeader(attributeId),
		sortable: true,
		filter: true,
		resizable: true,
		minWidth: 160,
		hide: true,
		columnGroupShow: 'open',
	};
}

function isDefaultDetailGridColumnHidden(colId) {
	return !defaultDetailGridVisibleColumns.value.includes(colId);
}

function getNormalizedAttributeValue(attribute) {
	if (attribute?.value === null || attribute?.value === undefined) {
		return '';
	}

	if (typeof attribute.value === 'object') {
		return JSON.stringify(attribute.value);
	}

	return String(attribute.value);
}

async function enrichRowsWithDeliveryLocationAttributes(rows = []) {
	if (!Array.isArray(rows) || rows.length === 0) {
		return { rows: [], columns: [] };
	}

	const deliveryLocationIds = [...new Set(rows.map((row) => getDeliveryLocationIdFromRow(row)).filter(Boolean))];
	if (!deliveryLocationIds.length) {
		return { rows, columns: [] };
	}

	const attributeItems = await getCachedDeliveryLocationAttributes(deliveryLocationIds);

	const rowsByDeliveryLocationId = new Map();
	const enrichedRows = rows.map((row) => {
		const rowCopy = { ...row };
		const deliveryLocationId = getDeliveryLocationIdFromRow(rowCopy);
		if (deliveryLocationId) {
			rowsByDeliveryLocationId.set(deliveryLocationId, rowCopy);
		}
		return rowCopy;
	});

	const columnsByAttributeId = new Map();

	attributeItems.forEach((item) => {
		const deliveryLocationId = getDeliveryLocationIdFromAttributeItem(item);
		if (!deliveryLocationId) {
			return;
		}

		const targetRow = rowsByDeliveryLocationId.get(deliveryLocationId);
		if (!targetRow) {
			return;
		}

		const attributes = Array.isArray(item?.attributes) ? item.attributes : [];
		attributes.forEach((attribute) => {
			const attributeId = getAttributeId(attribute);
			if (!attributeId) {
				return;
			}

			if (!columnsByAttributeId.has(attributeId)) {
				columnsByAttributeId.set(attributeId, buildAttributeColumn(attributeId));
			}

			targetRow[getAttributeField(attributeId)] = getNormalizedAttributeValue(attribute);
		});
	});

	const columns = [...columnsByAttributeId.entries()].sort(([firstId], [secondId]) => firstId - secondId).map(([, column]) => column);
	return { rows: enrichedRows, columns };
}

async function getCachedDeliveryLocationAttributes(deliveryLocationIds = []) {
	const cacheKey = [...deliveryLocationIds].sort((left, right) => left - right).join('|');
	if (deliveryLocationAttributeCache.has(cacheKey)) {
		return deliveryLocationAttributeCache.get(cacheKey);
	}

	try {
		const response = await getDeliveryLocationAttributes(deliveryLocationIds);
		const attributeItems = Array.isArray(response) ? response : [];
		deliveryLocationAttributeCache.set(cacheKey, attributeItems);
		return attributeItems;
	} catch {
		return [];
	}
}

async function resetFilters() {
	Object.values(filterRefs).forEach((filterRef) => {
		if (filterRef.value && typeof filterRef.value.isRequired === 'function' && filterRef.value.isRequired()) return;
		if (filterRef.value && typeof filterRef.value.reset === 'function') {
			filterRef.value.reset();
		}
	});

	allPark.value = false;
	selectedFilterChips.value = [];
	kpiDetailSearch.value = '';
	hasAppliedFilters.value = true;
	filterControls.value?.refreshActiveCount();
	if (showKpiDetailGrid.value) {
		await reloadDetailsPreservingProductGrain(buildFiltersFromControls());
	}
}

async function applyFilters(options = {}) {
	const skipPersistGeneratedCustomKpi = options?.skipPersistGeneratedCustomKpi === true;
	const selectedKpi = kpiReferenceSelect.value?.getFullValues();
	if (!selectedKpi) {
		error.value = t('PleaseSelectKpi');
		return;
	}

	error.value = null;
	hasAppliedFilters.value = true;

	// A different measure is a different grid altogether, so it legitimately drops the product grain
	// (RG-19); a filter-only apply must not — see reloadDetailsPreservingProductGrain below.
	const isMeasureChange = !hasSameReference(selectedKpi.reference, activeKpiReference.value);

	if (isMeasureChange) {
		const newWidgets = Array.isArray(sortedWidgets.value) ? sortedWidgets.value.filter((w) => hasSameReference(w?.params?.reference, selectedKpi.reference)) : [];
		const newWidget = newWidgets.find((w) => !w.guid) || newWidgets.find((w) => w.id === props.guid) || newWidgets[0] || null;
		isDetailSimulated.value = newWidget?.params && 'simulated' in newWidget.params ? Boolean(newWidget.params.simulated) : false;
	}

	activeKpiReference.value = selectedKpi.reference;

	const filters = buildFiltersFromControls(selectedKpi);
	currentAppliedFilters.value = { ...filters };
	isDetailSimulated.value = Boolean(filters?.simulated);
	selectedFilterChips.value = getSelectedFilterChips({
		organisations: organisationTreeview.value?.getFullValues() || [],
		deliveryLocationTypes: deliveryLocationTypeSelect.value?.getFullValues() || [],
		accounts: accountSelect.value?.getFullValues() || [],
		channels: channelSelect.value?.getFullValues() || [],
		statementProductTrees: statementProductTreeTreeview.value?.getFullValues() || [],
		productCategories: productTreeTreeview.value?.getFullValues() || [],
		specificities: specificitySelect.value?.getFullValues() || [],
		companies: companySelect.value?.getFullValues() || [],
		labels: labelTokens.value?.getFullValues() || [],
		eans: eanTokens.value?.getFullValues() || [],
		references: referenceTokens.value?.getFullValues() || [],
		excludeSharedOrganisations: Boolean(excludeSharedOrganisationsToggle.value?.getValues()),
	});

	if (showKpiDetailGrid.value) {
		if (isMeasureChange) {
			await loadKpiDetails(filters);
		} else {
			await reloadDetailsPreservingProductGrain(filters);
		}
	}

	if (!skipPersistGeneratedCustomKpi) {
		await persistGeneratedCustomKpi(filters, selectedKpi);
	}

	bumpViewStateVersion();
}

async function showDetailGrid() {
	showKpiDetailGrid.value = true;
	const filters = buildFiltersFromControls() || buildFiltersFromParams(currentAppliedFilters.value || props.params);
	if (!filters?.reference) {
		return;
	}
	await loadKpiDetails(filters);
	await nextTick();
	await waitForDetailGridReady();
	applyDetailRowGrouping();
}

async function reapplyKpiFilters(params = {}) {
	const filters = buildFiltersFromParams(params);
	if (!filters?.reference) {
		return;
	}

	filters.allPark = allPark.value;

	error.value = null;
	activeKpiReference.value = filters.reference;
	showKpiDetailGrid.value = true;
	await syncFiltersFromParams(filters);
	await loadKpiDetails(filters);
}

async function onKpiSimulatedChanged({ guid, reference, isSimulated }) {
	if (!reference || !hasSameReference(reference, activeKpiReference.value)) {
		return;
	}

	let sourceParams = mainKpiParams.value;
	if (guid) {
		const matchingCustomKpi = kpiComponents.value.find((customKpi) => customKpi.guid === guid);
		sourceParams = matchingCustomKpi?.params || sourceParams;
	}

	const filters = buildFiltersFromParams({
		...(sourceParams || {}),
		reference,
		simulated: !!isSimulated,
	});

	if (!filters?.reference) {
		return;
	}

	error.value = null;
	activeKpiReference.value = filters.reference;
	isDetailSimulated.value = Boolean(filters.simulated);
	if (showKpiDetailGrid.value) {
		await loadKpiDetails(filters);
	}
	bumpViewStateVersion();
}

function getKpiDetailRowId(params) {
	const row = params?.data || {};
	return String(row.id ?? `${row.deliveryLocationId ?? 'dl'}-${row.productTreeId ?? row.reference ?? `${row.companyName || ''}-${row.city || ''}`}`);
}

function updateSelectedFilterChips() {
	selectedFilterChips.value = getSelectedFilterChips({
		organisations: organisationTreeview.value?.getFullValues() || [],
		deliveryLocationTypes: deliveryLocationTypeSelect.value?.getFullValues() || [],
		accounts: accountSelect.value?.getFullValues() || [],
		channels: channelSelect.value?.getFullValues() || [],
		statementProductTrees: statementProductTreeTreeview.value?.getFullValues() || [],
		productCategories: productTreeTreeview.value?.getFullValues() || [],
		specificities: specificitySelect.value?.getFullValues() || [],
		companies: companySelect.value?.getFullValues() || [],
		labels: labelTokens.value?.getFullValues() || [],
		eans: eanTokens.value?.getFullValues() || [],
		references: referenceTokens.value?.getFullValues() || [],
		excludeSharedOrganisations: Boolean(excludeSharedOrganisationsToggle.value?.getValues()),
	});
}

async function onCustomKpiRemoved({ guid }) {
	if (!guid) {
		return;
	}

	localKpiComponents.value = localKpiComponents.value.filter((customKpi) => customKpi.guid !== guid);
	await savePersistedCustomKpis();
}

function hasSameReference(firstReference, secondReference) {
	if (!firstReference || !secondReference) {
		return false;
	}

	return firstReference.toLowerCase() === secondReference.toLowerCase();
}

function findKpiByReference(reference) {
	if (!reference) {
		return null;
	}

	return kpis.value.find((kpiItem) => hasSameReference(kpiItem.reference, reference)) || null;
}

function getKpiTitle(reference) {
	return findKpiByReference(reference)?.reference || null;
}

function mapWidgetToCustomKpi(widget) {
	return {
		title: getKpiTitle(widget.params?.reference) || widget.params?.reference || widget.title,
		color: props.color,
		width: props.width,
		params: widget.params,
		annotation: widget.title,
		custom: true,
		guid: widget.guid || widget.id,
	};
}

function mapStoredCustomKpi(customKpi) {
	const params = buildFiltersFromParams(customKpi?.params || customKpi);
	if (!params?.reference) {
		return null;
	}

	return {
		title: customKpi.title || getKpiTitle(params.reference) || params.reference,
		color: customKpi.color || 'primary',
		width: Number.isFinite(Number(customKpi.width)) ? Number(customKpi.width) : 12,
		params,
		annotation: customKpi.annotation || getAnnotationForFilters(params),
		custom: true,
		guid: customKpi.guid || crypto.randomUUID(),
	};
}

function mapCustomKpiToSetting(customKpi) {
	const params = buildFiltersFromParams(customKpi?.params);
	if (!params?.reference) {
		return null;
	}

	return {
		guid: customKpi.guid || crypto.randomUUID(),
		title: customKpi.title || getKpiTitle(params.reference) || params.reference,
		color: customKpi.color || 'primary',
		width: Number.isFinite(Number(customKpi.width)) ? Number(customKpi.width) : 12,
		annotation: customKpi.annotation || getAnnotationForFilters(params),
		params,
	};
}

function buildGeneratedCustomKpi(filters, selectedKpi) {
	if (!filters?.reference) {
		return null;
	}

	return {
		title: getKpiTitle(selectedKpi?.reference || filters.reference) || selectedKpi?.reference || filters.reference,
		color: 'primary',
		width: 12,
		params: filters,
		annotation: getAnnotationForFilters(filters),
		custom: true,
		guid: crypto.randomUUID(),
	};
}

function customKpiAlreadyExists(filters) {
	return localKpiComponents.value.some((customKpi) => {
		if (!hasSameReference(customKpi.params?.reference, filters.reference)) {
			return false;
		}

		const customFilters = {
			organisations: (customKpi.params?.organisations || []).sort().join('|'),
			dlts: (customKpi.params?.dlts || customKpi.params?.deliveryLocationTypes || []).sort().join('|'),
			accounts: (customKpi.params?.accounts || []).sort().join('|'),
			channels: (customKpi.params?.channels || []).sort().join('|'),
			statementProductTrees: (customKpi.params?.statementProductTrees || customKpi.params?.productTrees || customKpi.params?.pts || []).sort().join('|'),
			productCategories: (customKpi.params?.productCategories || []).sort().join('|'),
			specificities: (customKpi.params?.specificities || customKpi.params?.spes || []).sort().join('|'),
			companies: (customKpi.params?.companies || []).sort().join('|'),
			labels: (customKpi.params?.labels || []).sort().join('|'),
			eans: (customKpi.params?.eans || []).sort().join('|'),
			references: (customKpi.params?.references || []).sort().join('|'),
			excludeSharedOrganisations: Boolean(customKpi.params?.excludeSharedOrganisations),
			simulated: customKpi.params?.simulated,
		};

		const currentFilters = {
			organisations: (filters.organisations || []).sort().join('|'),
			dlts: (filters.dlts || filters.deliveryLocationTypes || []).sort().join('|'),
			accounts: (filters.accounts || []).sort().join('|'),
			channels: (filters.channels || []).sort().join('|'),
			statementProductTrees: (filters.statementProductTrees || filters.productTrees || filters.pts || []).sort().join('|'),
			productCategories: (filters.productCategories || []).sort().join('|'),
			specificities: (filters.specificities || filters.spes || []).sort().join('|'),
			companies: (filters.companies || []).sort().join('|'),
			labels: (filters.labels || []).sort().join('|'),
			eans: (filters.eans || []).sort().join('|'),
			references: (filters.references || []).sort().join('|'),
			excludeSharedOrganisations: Boolean(filters.excludeSharedOrganisations),
			simulated: filters.simulated,
		};

		return JSON.stringify(customFilters) === JSON.stringify(currentFilters);
	});
}

async function persistGeneratedCustomKpi(filters, selectedKpi) {
	const generatedCustomKpi = buildGeneratedCustomKpi(filters, selectedKpi);
	if (!generatedCustomKpi) {
		return;
	}

	if (customKpiAlreadyExists(filters)) {
		return;
	}

	const hasExistingCustomKpi = localKpiComponents.value.some((customKpi) => hasSameReference(customKpi.params?.reference, filters.reference));

	if (hasExistingCustomKpi) {
		return;
	}

	localKpiComponents.value.push(generatedCustomKpi);
	await savePersistedCustomKpis();
}

async function createCustomKpiManually() {
	const filters = buildFiltersFromControls();
	if (!filters?.reference) {
		error.value = t('PleaseSelectKpi');
		return;
	}

	const selectedKpi = kpiReferenceSelect.value?.getFullValues();
	const customKpi = buildGeneratedCustomKpi(filters, selectedKpi);
	if (!customKpi) {
		return;
	}

	localKpiComponents.value.push(customKpi);
	await savePersistedCustomKpis();
}

async function savePersistedCustomKpis() {
	const persistedCustomKpis = localKpiComponents.value.map((customKpi) => mapCustomKpiToSetting(customKpi)).filter(Boolean);
	userSettings.value = userSettings.value.filter((setting) => setting.name !== KPI_BUILDER_CUSTOMS_SETTING_NAME);
	userSettings.value.push({
		name: KPI_BUILDER_CUSTOMS_SETTING_NAME,
		groupName: 'WEBAPP',
		value: JSON.stringify(persistedCustomKpis),
	});

	const isSaved = await postUserSettings(userSettings.value, { groupName: 'WEBAPP', userId: user.id });
	if (!isSaved) {
		error.value = "Impossible d'enregistrer les KPIs personnalisés.";
	}
}

function formatKpiMetric(value, signed = false) {
	if (value === null || value === undefined || Number.isNaN(Number(value))) {
		return '';
	}

	const numericValue = Number(value);
	const roundedValue = Math.round(numericValue * 100) / 100;
	const displayValue = Number.isInteger(roundedValue) ? `${roundedValue}` : roundedValue.toFixed(2);
	const prefix = signed && roundedValue > 0 ? '+' : '';
	const suffix = isDetailPercentage.value ? '%' : '';
	return `${prefix}${displayValue}${suffix}`;
}

function formatReferenceCount(value) {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? `${Math.round(numericValue)}` : '';
}

function getDifferenceCellStyle(value) {
	if (value > 0) {
		return { color: 'rgb(var(--v-theme-success))', fontWeight: 600 };
	}

	if (value < 0) {
		return { color: 'rgb(var(--v-theme-error))', fontWeight: 600 };
	}

	return {};
}

function getKpiDetailGroupRowAgg(params) {
	const leafRows = collectLeafRows(params?.nodes || []);
	const categoryFields = Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => `productTreeLevel${index + 1}`);
	const dynamicAttributeFields = deliveryLocationAttributeColumns.value.map((column) => column.field);
	if (!leafRows.length) {
		const emptyCategoryCounts = categoryFields.reduce((counts, field) => {
			counts[field] = 0;
			return counts;
		}, {});
		const emptyDynamicCounts = dynamicAttributeFields.reduce((counts, field) => {
			counts[field] = 0;
			return counts;
		}, {});

		return {
			companyName: 0,
			deliveryLocation: 0,
			parentAccount: 0,
			account: 0,
			channel: 0,
			deliveryLocationType: 0,
			productTree: 0,
			specificities: 0,
			address: 0,
			postcode: 0,
			city: 0,
			organisations: 0,
			value: 0,
			objective: 0,
			difference: 0,
			referenceCount: 0,
			totalReferencesCount: 0,
			classColor: '',
			color: 'primary',
			...emptyCategoryCounts,
			...emptyDynamicCounts,
		};
	}

	const activeRows = leafRows.filter((row) => row?.isActive);
	const aggregationMode = (activeRows[0] || leafRows[0])?.aggregationMode || 'sum';
	const aggregatedCounts = buildCountAggregates(leafRows, ['companyName', 'deliveryLocation', 'parentAccount', 'account', 'channel', 'deliveryLocationType', 'productTree', 'specificities', 'address', 'postcode', 'city', 'posType', 'locationGroup', 'distributionChannel', 'seasonality', 'organisations', ...categoryFields, ...dynamicAttributeFields]);

	if (aggregationMode === 'ratio') {
		let ratioValue;
		let totalReferencesCountForGroup;

		if (currentAppliedFilters.value?.allPark && !currentAppliedFilters.value?.simulated) {
			// allPark real mode (tout inclure): numerator from visited rows, denominator from ALL rows.
			// Unvisited rows carry their (custom-KPI-filtered) référencement as totalReferencesCount,
			// computed in _buildKpiDetailRows, so they dilute the ratio exactly like the single-DL gauge.
			const numerator = getSumValue(activeRows.map((row) => row.referenceCount));
			const denominator = getSumValue(leafRows.map((row) => row.totalReferencesCount));
			ratioValue = denominator > 0 ? roundMetricValue((numerator / denominator) * 100) : 0;
			totalReferencesCountForGroup = getCountSum(leafRows.map((row) => row.totalReferencesCount));
		} else {
			ratioValue = getGroupedRatioValue(activeRows);
			totalReferencesCountForGroup = getCountSum(activeRows.map((row) => row.totalReferencesCount));
		}

		const rowsWithRealObjective = activeRows.filter((row) => row._hasRealObjective);
		const objective = rowsWithRealObjective.length > 0 ? roundMetricValue(getAverageValue(rowsWithRealObjective.map((row) => row.objective))) : 0;
		const { classColor, color } = getKpiColors(ratioValue, objective);
		return {
			...aggregatedCounts,
			value: ratioValue,
			objective,
			difference: roundMetricValue(ratioValue - objective),
			referenceCount: getCountSum(activeRows.map((row) => row.referenceCount)),
			totalReferencesCount: totalReferencesCountForGroup,
			classColor,
			color,
		};
	}

	const value = roundMetricValue(getSumValue(activeRows.map((row) => row.rawValue ?? row.value)));
	const rowsWithRealObjectiveForSum = activeRows.filter((row) => row._hasRealObjective);
	const objective = rowsWithRealObjectiveForSum.length > 0 ? roundMetricValue(getSumValue(rowsWithRealObjectiveForSum.map((row) => row.objective))) : 0;
	const { classColor, color } = getKpiColors(value, objective);
	// allPark real mode (tout inclure): unvisited stores carry their référencement in totalReferencesCount
	// too (isActive: false), so include all leaf rows here just like the ratio branch above.
	const totalReferencesCountForSum = currentAppliedFilters.value?.allPark && !currentAppliedFilters.value?.simulated ? getCountSum(leafRows.map((row) => row.totalReferencesCount)) : getCountSum(activeRows.map((row) => row.totalReferencesCount));
	return {
		...aggregatedCounts,
		value,
		objective,
		difference: roundMetricValue(value - objective),
		referenceCount: getCountSum(activeRows.map((row) => row.referenceCount)),
		totalReferencesCount: totalReferencesCountForSum,
		classColor,
		color,
	};
}

// Sibling to getKpiDetailGroupRowAgg (not a modification of it) for product-grain rows: same ratio/sum
// formulas via the same small pure helpers (getGroupedRatioValue/getAverageValue/buildCountAggregates),
// just fed product-fact leaves. No allPark-dilution branch needed — unlike store grain, an unvisited DL
// never contributes a placeholder product row, so every leaf here is already isActive:true.
function getKpiProductGrainGroupRowAgg(params) {
	const leafRows = collectLeafRows(params?.nodes || [], (data) => `${data.deliveryLocationId ?? 'dl'}-${data.productTreeId ?? 'pt'}-${data.productId ?? 'p'}`);
	const productCategoryFields = Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => `productCategoryLevel${index + 1}`);
	const statementCategoryFields = Array.from({ length: productTreeMaxLevel.value || 0 }, (_, index) => `productTreeLevel${index + 1}`);
	const dynamicAttributeFields = deliveryLocationAttributeColumns.value.map((column) => column.field);
	const countFields = ['companyName', 'deliveryLocation', 'parentAccount', 'account', 'channel', 'deliveryLocationType', 'address', 'postcode', 'city', 'organisations', 'label', 'reference', 'ean', 'productCategory', 'specificities', ...productCategoryFields, ...statementCategoryFields, ...dynamicAttributeFields];

	if (!leafRows.length) {
		return countFields.reduce((counts, field) => ({ ...counts, [field]: 0 }), { value: 0, objective: 0, difference: 0, referenceCount: 0, totalReferencesCount: 0, classColor: '', color: 'primary' });
	}

	const activeRows = leafRows;
	const aggregationMode = activeRows[0]?.aggregationMode || 'sum';
	const aggregatedCounts = buildCountAggregates(leafRows, countFields);

	if (aggregationMode === 'ratio') {
		const ratioValue = getGroupedRatioValue(activeRows);
		const rowsWithRealObjective = activeRows.filter((row) => row._hasRealObjective);
		const objective = rowsWithRealObjective.length > 0 ? roundMetricValue(getAverageValue(rowsWithRealObjective.map((row) => row.objective))) : 0;
		const { classColor, color } = getKpiColors(ratioValue, objective);
		return {
			...aggregatedCounts,
			value: ratioValue,
			objective,
			difference: roundMetricValue(ratioValue - objective),
			referenceCount: getCountSum(activeRows.map((row) => row.referenceCount)),
			totalReferencesCount: getCountSum(activeRows.map((row) => row.totalReferencesCount)),
			classColor,
			color,
		};
	}

	const value = roundMetricValue(getSumValue(activeRows.map((row) => row.rawValue ?? row.value)));
	const rowsWithRealObjectiveForSum = activeRows.filter((row) => row._hasRealObjective);
	const objective = rowsWithRealObjectiveForSum.length > 0 ? roundMetricValue(getSumValue(rowsWithRealObjectiveForSum.map((row) => row.objective))) : 0;
	const { classColor, color } = getKpiColors(value, objective);
	return {
		...aggregatedCounts,
		value,
		objective,
		difference: roundMetricValue(value - objective),
		referenceCount: getCountSum(activeRows.map((row) => row.referenceCount)),
		totalReferencesCount: getCountSum(activeRows.map((row) => row.totalReferencesCount)),
		classColor,
		color,
	};
}

function getKpiColors(value, objective) {
	const reference = activeKpiReference.value || props.params.reference || '';
	if (!reference || typeof kpiService.defineColors !== 'function') {
		return { classColor: '', color: 'primary' };
	}

	const kpiDefinition = findKpiByReference(reference);
	const tolerance = Number(kpiDefinition?.tolerance ?? 0);
	const isInverted = typeof kpiService._isInvertedKpi === 'function' ? kpiService._isInvertedKpi(reference) : false;
	return kpiService.defineColors(value, objective, isInverted, tolerance);
}

// getRowKey defaults to the store-grain dedup key (unchanged behaviour for getKpiDetailGroupRowAgg);
// getKpiProductGrainGroupRowAgg passes one that also folds in productId.
function collectLeafRows(nodes = [], getRowKey = (data) => `${data.deliveryLocationId ?? 'dl'}-${data.productTreeId ?? 'pt'}`) {
	const rows = [];
	const seenRowIds = new Set();

	const visitNode = (node) => {
		if (!node) {
			return;
		}

		const children = node.childrenAfterFilter || node.childrenAfterGroup || node.childrenAfterSort;
		if (Array.isArray(children) && children.length > 0) {
			children.forEach(visitNode);
			return;
		}

		if (!node.data) {
			return;
		}

		// Dedup so stores shared across multiple organisations (cloned via _organisationPaths into rows
		// with `__orgN` suffixed ids) are counted only once at parent group levels.
		const rowKey = getRowKey(node.data);
		if (seenRowIds.has(rowKey)) {
			return;
		}

		seenRowIds.add(rowKey);
		rows.push(node.data);
	};

	nodes.forEach(visitNode);
	return rows;
}

function getGroupedRatioValue(rows = []) {
	const numerator = getSumValue(rows.map((row) => row.referenceCount));
	const denominator = getSumValue(rows.map((row) => row.totalReferencesCount));

	if (!denominator) {
		return 0;
	}

	return roundMetricValue((numerator / denominator) * 100);
}

function buildCountAggregates(rows = [], fields = []) {
	return fields.reduce((aggregates, field) => {
		aggregates[field] = countNonEmptyValues(rows, field);
		return aggregates;
	}, {});
}

function countNonEmptyValues(rows = [], field) {
	return rows.reduce((count, row) => {
		const value = row?.[field];
		if (value === null || value === undefined || value === '') {
			return count;
		}

		return count + 1;
	}, 0);
}

function getAverageValue(values = []) {
	const numericValues = values.map((value) => Number(value)).filter((value) => Number.isFinite(value));

	if (!numericValues.length) {
		return 0;
	}

	return getSumValue(numericValues) / numericValues.length;
}

function getSumValue(values = []) {
	return values.reduce((sum, value) => {
		const numericValue = Number(value);
		return Number.isFinite(numericValue) ? sum + numericValue : sum;
	}, 0);
}

function getCountSum(values = []) {
	return Math.round(getSumValue(values));
}

function roundMetricValue(value) {
	const numericValue = Number(value);
	return Number.isFinite(numericValue) ? Number(numericValue.toFixed(2)) : 0;
}

function getAnnotationForFilters(filters) {
	const parts = [];

	if (filters.organisations && filters.organisations.length > 0) {
		if (filters.organisations.length === 1) {
			const org = organisationTreeview.value?.getFullValues()?.[0];
			parts.push(`${org ? org.label : filters.organisations[0]}`);
		} else {
			parts.push(`${t('Organisations')}: ${filters.organisations.length}`);
		}
	}

	if (filters.dlts && filters.dlts.length > 0) {
		if (filters.dlts.length === 1) {
			const deliveryLocationType = deliveryLocationTypeSelect.value?.getFullValues()?.[0];
			parts.push(`${deliveryLocationType ? deliveryLocationType.name : filters.dlts[0]}`);
		} else {
			parts.push(`${t('DeliveryLocationType')}: ${filters.dlts.length}`);
		}
	}

	if (filters.accounts && filters.accounts.length > 0) {
		if (filters.accounts.length === 1) {
			const account = accountSelect.value?.getFullValues()?.[0];
			parts.push(`${account ? account.name : filters.accounts[0]}`);
		} else {
			parts.push(`${t('Accounts')}: ${filters.accounts.length}`);
		}
	}

	if (filters.channels && filters.channels.length > 0) {
		if (filters.channels.length === 1) {
			const channel = channelSelect.value?.getFullValues()?.[0];
			parts.push(`${channel ? channel.name : filters.channels[0]}`);
		} else {
			parts.push(`${t('Channels')}: ${filters.channels.length}`);
		}
	}

	if (filters.statementProductTrees && filters.statementProductTrees.length > 0) {
		if (filters.statementProductTrees.length === 1) {
			const statementProductTree = statementProductTreeTreeview.value?.getFullValues()?.[0];
			parts.push(`${statementProductTree ? statementProductTree.label : filters.statementProductTrees[0]}`);
		} else {
			parts.push(`${t('StatementCategories')}: ${filters.statementProductTrees.length}`);
		}
	}

	if (filters.productCategories && filters.productCategories.length > 0) {
		if (filters.productCategories.length === 1) {
			const productTree = productTreeTreeview.value?.getFullValues()?.[0];
			parts.push(`${productTree ? productTree.label : filters.productCategories[0]}`);
		} else {
			parts.push(`${t('ProductCategory')}: ${filters.productCategories.length}`);
		}
	}

	if (filters.specificities && filters.specificities.length > 0) {
		if (filters.specificities.length === 1) {
			const specificity = specificitySelect.value?.getFullValues()?.[0];
			parts.push(`${specificity ? specificity.value : filters.specificities[0]}`);
		} else {
			parts.push(`${t('Specificities')}: ${filters.specificities.length}`);
		}
	}

	if (filters.companies && filters.companies.length > 0) {
		if (filters.companies.length === 1) {
			const company = companySelect.value?.getFullValues()?.[0];
			parts.push(`${company?.companyName || filters.companies[0]}`);
		} else {
			parts.push(`${t('Companies')}: ${filters.companies.length}`);
		}
	}

	if (filters.labels?.length > 0) {
		parts.push(filters.labels.length === 1 ? filters.labels[0] : `${t('ProductLabel')}: ${filters.labels.length}`);
	}

	if (filters.eans?.length > 0) {
		parts.push(filters.eans.length === 1 ? filters.eans[0] : `${t('Ean')}: ${filters.eans.length}`);
	}

	if (filters.references?.length > 0) {
		parts.push(filters.references.length === 1 ? filters.references[0] : `${t('ProductReference')}: ${filters.references.length}`);
	}

	if (filters.excludeSharedOrganisations) {
		parts.push(t('ExcludeSharedOrganisations'));
	}

	if (parts.length === 0) {
		return t('KpiCustom');
	}

	return parts.join(' | ');
}

// `valueKey` per entry must mirror the control's own `titleKey` (see filterDefinitions): companies are
// titled by `companyName`, and passing `name` here made the chip fall through to the raw company id.
function getSelectedFilterChips(selections) {
	const chips = buildFilterChips([
		{ key: 'organisationTreeview', label: t('Organisations'), values: selections.organisations, valueKey: 'label' },
		{ key: 'deliveryLocationTypeSelect', label: t('DeliveryLocationType'), values: selections.deliveryLocationTypes, valueKey: 'name' },
		{ key: 'accountSelect', label: t('Accounts'), values: selections.accounts, valueKey: 'name' },
		{ key: 'channelSelect', label: t('Channels'), values: selections.channels, valueKey: 'name' },
		{ key: 'companySelect', label: t('Companies'), values: selections.companies, valueKey: 'companyName' },
		{ key: 'statementProductTreeTreeview', label: t('StatementCategories'), values: selections.statementProductTrees, valueKey: 'label' },
		{ key: 'productTreeTreeview', label: t('ProductCategory'), values: selections.productCategories, valueKey: 'label' },
		{ key: 'specificitySelect', label: t('Specificities'), values: selections.specificities, valueKey: 'value' },
		{ key: 'labelTokens', label: t('ProductLabel'), values: selections.labels },
		{ key: 'eanTokens', label: t('Ean'), values: selections.eans },
		{ key: 'referenceTokens', label: t('ProductReference'), values: selections.references },
	]);

	if (selections.excludeSharedOrganisations) {
		chips.push({ key: 'excludeSharedOrganisationsToggle', kind: 'state', label: t('ExcludeSharedOrganisations') });
	}

	return chips;
}

// Opens the panel/sheet and scrolls the corresponding field into view — distinct from the chip's own
// close (✕), which fully resets the underlying control (reused unchanged below).
async function onFilterChipClick(filterKey) {
	isFilterSheetOpen.value = true;
	await nextTick();
	setTimeout(() => scrollFilterFieldIntoView(filterKey, overlayTarget), 150);
}

async function removeFilterChip(filterKey) {
	const filterMap = {
		organisationTreeview,
		deliveryLocationTypeSelect,
		accountSelect,
		channelSelect,
		companySelect,
		statementProductTreeTreeview,
		productTreeTreeview,
		specificitySelect,
		labelTokens,
		eanTokens,
		referenceTokens,
		excludeSharedOrganisationsToggle,
	};
	const targetFilterRef = filterMap[filterKey]?.value;

	if (targetFilterRef && typeof targetFilterRef.reset === 'function') {
		targetFilterRef.reset();
	}

	selectedFilterChips.value = selectedFilterChips.value.filter((chip) => chip.key !== filterKey);
	filterControls.value?.refreshActiveCount();
	await reloadDetailsPreservingProductGrain(buildFiltersFromControls());
}
</script>
